import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, History, RefreshCw, UploadCloud } from 'lucide-react';
import { getAuthHeader, getUser } from "../utils/auth"; 
import EContractTab from '../components/contracts/EContractTab';
import { isEnglishOperatorMode, withOperatorSurface } from '../utils/operatorMode';

// --- SVG Icons ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const SettingsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>;
const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>;
const BookmarkIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>;
const CheckSquareIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>;
const ReceiptIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>;
const UndoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"></path><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path></svg>;
// ★ 追加: 新機能用のアイコン
const PackageIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>;
const LockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-slate-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>;
const UserIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const CreditCardIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
        <line x1="1" y1="10" x2="23" y2="10"></line>
    </svg>
);
const EyeIcon = ({ size = 16, className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);
// --- SVG Icons ---
const FileTextIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><line x1="10" y1="9" x2="8" y2="9"></line></svg>;
const WandIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l10-10"/><path d="m15 6 3-3"/><path d="m17 2 4 4"/><path d="m19 9 3-3"/></svg>;
const SparklesIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 3.8-3.8 1.9 3.8 1.9L12 14l1.9-3.8 3.8-1.9-3.8-1.9L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>;

// GemのURL定義
const CONTRACT_GEM_URLS = {
    CRAFTSMAN: "https://gemini.google.com/gem/1jA58_DqoYvIW6qbumskdQqM1ZOKabhYB?usp=sharing",
    FIXER: "https://gemini.google.com/gem/1arReUL6BT2ENvLO1hbKUST-UT-7lP-3J?usp=sharing"
};

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

// 獲得経路のマッピング
const ACQUISITION_SOURCE_MAP = {
    form: 'フォーム',
    telephone: 'テレアポ',
    referral: '紹介',
    agency: '代理店',
    inquiry: 'Web問い合わせ',
    event: '展示会・イベント',
    web_ads: 'web広告',
    sns: 'SNS',
    other: 'その他'
};

const ACQUISITION_SOURCE_MAP_EN = {
    form: 'Form',
    telephone: 'Phone outreach',
    referral: 'Referral',
    agency: 'Agency',
    inquiry: 'Web inquiry',
    event: 'Event / trade show',
    web_ads: 'Web ads',
    sns: 'Social media',
    other: 'Other'
};

const CUSTOMER_LEDGER_COPY = {
    ja: {
        pageTitle: '顧客台帳 (CRM)',
        importHistory: 'インポート履歴',
        csvImport: 'CSVインポート',
        productMaster: '商品マスタ管理',
        contractTemplate: '契約書雛形管理',
        billingSettings: '請求設定',
        newCustomer: '新規登録',
        searchPlaceholder: '会社名、担当者、電話、メモ...',
        nextAction: '次回アクション:',
        search: '検索',
        saveCondition: 'この条件を保存',
        selected: '件 選択中',
        runBulk: '一括操作を実行',
        noCustomers: '顧客データがありません。',
        companyName: '会社名',
        internalOwner: '社内担当者',
        contractType: '契約形態',
        representative: '代表者',
        representativeMissing: '代表者未登録',
        status: 'ステータス',
        nextActionCol: '次回アクション',
        lastUpdated: '最終更新',
        details: '詳細',
        unassigned: '未割り当て',
        active: '有効',
        inactive: '無効',
        allCountPrefix: '全',
        allCountSuffix: '件',
        freeLimit: ' (無料プランのため50件まで表示)',
        createdCustomer: '新規顧客を登録しました！',
        errorPrefix: 'エラー: '
    },
    en: {
        pageTitle: 'Customer Ledger (CRM)',
        importHistory: 'Import History',
        csvImport: 'CSV Import',
        productMaster: 'Product Master',
        contractTemplate: 'Contract Templates',
        billingSettings: 'Billing Settings',
        newCustomer: 'New Customer',
        searchPlaceholder: 'Company, contact, phone, memo...',
        nextAction: 'Next action:',
        search: 'Search',
        saveCondition: 'Save this search',
        selected: 'selected',
        runBulk: 'Run bulk action',
        noCustomers: 'No customer records yet.',
        companyName: 'Company',
        internalOwner: 'Owner',
        contractType: 'Contract Type',
        representative: 'Representative',
        representativeMissing: 'No representative',
        status: 'Status',
        nextActionCol: 'Next Action',
        lastUpdated: 'Last Updated',
        details: 'Details',
        unassigned: 'Unassigned',
        active: 'Active',
        inactive: 'Inactive',
        allCountPrefix: 'Total',
        allCountSuffix: 'records',
        freeLimit: ' (Free plan shows up to 50 records)',
        createdCustomer: 'Customer created.',
        errorPrefix: 'Error: '
    }
};

const getLedgerCopy = (isEnglishBeta) => isEnglishBeta ? CUSTOMER_LEDGER_COPY.en : CUSTOMER_LEDGER_COPY.ja;

const getAcquisitionSourceLabel = (key, isEnglishBeta) => (
    isEnglishBeta ? ACQUISITION_SOURCE_MAP_EN[key] : ACQUISITION_SOURCE_MAP[key]
) || key;

const getContractTypeLabel = (value, isEnglishBeta, short = false) => {
    const labels = {
        ja: {
            spot: short ? '単発' : '単発 (Shot)',
            subscription: short ? 'サブスク' : 'サブスクリプション',
            consulting: short ? 'コンサル' : 'コンサルティング'
        },
        en: {
            spot: short ? 'One-time' : 'One-time',
            subscription: short ? 'Recurring' : 'Subscription',
            consulting: short ? 'Consulting' : 'Consulting'
        }
    };
    return (isEnglishBeta ? labels.en : labels.ja)[value] || value || '-';
};

const getContractStatusLabel = (value, isEnglishBeta) => {
    const labels = {
        ja: { active: '契約中', pending: '交渉中', cancelled: '解約', completed: '完了' },
        en: { active: 'Active', pending: 'Negotiating', cancelled: 'Canceled', completed: 'Completed' }
    };
    return (isEnglishBeta ? labels.en : labels.ja)[value] || value || '-';
};

const getInvoiceTypeLabel = (value, isEnglishBeta) => {
    const labels = {
        ja: { all: 'すべて', quotation: '見積書', invoice: '請求書', receipt: '領収書' },
        en: { all: 'All', quotation: 'Quote', invoice: 'Invoice', receipt: 'Receipt' }
    };
    return (isEnglishBeta ? labels.en : labels.ja)[value] || value || '-';
};

const getBillingIntervalLabelText = (subscription, isEnglishBeta) => {
    const count = Math.max(1, Number(subscription?.interval_count) || 1);
    const isYear = subscription?.interval_unit === 'year';
    if (isEnglishBeta) {
        const unit = isYear ? 'year' : 'month';
        return count === 1 ? `per ${unit}` : `every ${count} ${unit}s`;
    }
    const unit = isYear ? '年' : 'ヶ月';
    return count === 1 ? `毎${unit}` : `${count}${unit}ごと`;
};

// --- Components ---

const LoadingSpinner = () => (
    <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
);

const CUSTOMER_IMPORT_MODE_OPTIONS = [
    { value: 'skip', label: '重複は登録しない' },
    { value: 'fill_empty', label: '空欄だけ補完' },
    { value: 'tag_only', label: 'タグだけ追記' },
    { value: 'overwrite', label: '入力値で上書き' }
];

const CUSTOMER_IMPORT_MODE_OPTIONS_EN = [
    { value: 'skip', label: 'Do not import duplicates' },
    { value: 'fill_empty', label: 'Fill empty fields only' },
    { value: 'tag_only', label: 'Append tags only' },
    { value: 'overwrite', label: 'Overwrite with CSV values' }
];

const CUSTOMER_IMPORT_ACTION_LABELS = {
    insert: '新規',
    update_candidate: '重複候補',
    skip: 'スキップ',
    error: 'エラー'
};

const CUSTOMER_IMPORT_ACTION_LABELS_EN = {
    insert: 'New',
    update_candidate: 'Duplicate Candidate',
    skip: 'Skipped',
    error: 'Error',
    inserted: 'Inserted',
    updated: 'Updated',
    tag_appended: 'Tag Appended'
};

const getCustomerImportModeOptions = (isEnglishBeta) =>
    isEnglishBeta ? CUSTOMER_IMPORT_MODE_OPTIONS_EN : CUSTOMER_IMPORT_MODE_OPTIONS;

const getCustomerImportActionLabel = (action, isEnglishBeta) =>
    (isEnglishBeta ? CUSTOMER_IMPORT_ACTION_LABELS_EN : CUSTOMER_IMPORT_ACTION_LABELS)[action] || action;

const getCustomerImportActionClass = (action) => {
    if (action === 'insert' || action === 'inserted') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (action === 'update_candidate' || action === 'updated' || action === 'tag_appended') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (action === 'error') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
};

const formatCustomerImportDateTime = (value, isEnglishBeta = false) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(isEnglishBeta ? 'en-US' : 'ja-JP', { timeZone: 'Asia/Tokyo' });
};

const downloadAuthenticatedFile = async (url, filename) => {
    const res = await fetch(url, { headers: getAuthHeader() });
    if (!res.ok) {
        let message = 'ダウンロードに失敗しました。';
        try {
            const data = await res.json();
            message = data.error || message;
        } catch {
            const text = await res.text();
            if (text) message = text;
        }
        throw new Error(message);
    }
    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
};

// ★ 修正: userを受け取り、OEMユーザーの場合はリンクを隠す
const UpgradeModal = ({ onClose, user, isEnglishBeta = false, accountPath = '/account' }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-white p-8 rounded-lg shadow-2xl border w-full max-w-md text-center relative" onClick={e => e.stopPropagation()}>
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><CloseIcon /></button>
            <div className="flex justify-center"><LockIcon /></div>
            <h3 className="text-xl font-bold text-slate-800 mt-2">{isEnglishBeta ? 'This feature requires a paid plan' : '有料プラン限定機能です'}</h3>
            <p className="text-slate-600 mt-4 text-sm">
                {isEnglishBeta ? (
                    <>Upgrade your plan to search customers, view additional pages, and use CRM analysis features.</>
                ) : (
                    <>顧客の検索、2ページ目以降の閲覧、分析機能などを利用するには<br/>プランのアップグレードが必要です。</>
                )}
            </p>
            
            {/* 🚀 修正: OEMユーザーかどうかで表示を出し分け */}
            {!user?.oem_id ? (
                <Link to={isEnglishBeta ? withOperatorSurface(accountPath) : accountPath} className="mt-6 inline-block bg-green-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-700 transition-colors w-full">
                    {isEnglishBeta ? 'Review Plan' : '🚀 プランを確認する'}
                </Link>
            ) : (
                <div className="mt-6 p-4 bg-blue-50 text-blue-700 font-bold rounded-xl border border-blue-100">
                    {isEnglishBeta ? 'Please contact your contract owner about upgrades.' : 'アップグレードについては、ご契約先の担当者までお問い合わせください。'}
                </div>
            )}
        </div>
    </div>
);

// ★ 修正: ページネーション (無料プラン制限対応)
function Pagination({ currentPage, totalPages, onPageChange, isFreePlan, onLimitTrigger, isEnglishBeta = false }) {
    if (totalPages <= 1) return null;
    
    const handlePageClick = (page) => {
        if (isFreePlan && page > 1) {
            onLimitTrigger();
        } else {
            onPageChange(page);
        }
    };

    return (
        <div className="flex items-center justify-center space-x-2 mt-4">
            <button onClick={() => handlePageClick(1)} disabled={currentPage === 1} className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">{isEnglishBeta ? 'First' : '最初'}</button>
            <button onClick={() => handlePageClick(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">{isEnglishBeta ? 'Previous' : '前へ'}</button>
            <span className="text-sm text-gray-600 px-2">{currentPage} / {totalPages}</span>
            <button onClick={() => handlePageClick(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">
                {isEnglishBeta ? 'Next' : '次へ'} {isFreePlan && <span className="ml-1 text-xs">🔒</span>}
            </button>
        </div>
    );
}

// --- Sub-Components ---

// ★ 追加: 商品マスタ管理モーダル (Billing Products)
const ProductManagementModal = ({ onClose, client_id, isEnglishBeta = false }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingProduct, setEditingProduct] = useState(null); // null = 一覧モード, obj = 編集モード, {} = 新規
    const [form, setForm] = useState({ name: '', unit_price: 0, cost_price: 0, tax_rate: 10 });

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/crm/billing-products?client_id=${client_id}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) setProducts(data.products || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [client_id]);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    const handleSave = async (e) => {
        e.preventDefault();
        const isUpdate = editingProduct && editingProduct.id;
        const url = isUpdate 
            ? `${API_URL}/api/crm/billing-products/${editingProduct.id}` 
            : `${API_URL}/api/crm/billing-products`;
        const method = isUpdate ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ ...form, client_id })
            });
            if (res.ok) {
                setEditingProduct(null);
                fetchProducts();
            } else {
                alert(isEnglishBeta ? 'Failed to save.' : '保存に失敗しました');
            }
        } catch(e) { alert(e.message); }
    };

    const handleDelete = async (id) => {
        if(!window.confirm(isEnglishBeta ? 'Delete this product?' : '削除しますか？')) return;
        try {
            await fetch(`${API_URL}/api/crm/billing-products/${id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ client_id })
            });
            fetchProducts();
        } catch(e) { alert(e.message); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <PackageIcon /> {isEnglishBeta ? 'Billing Product Master' : '請求用商品マスタ設定'}
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><CloseIcon /></button>
                </div>
                
                <div className="p-6 overflow-y-auto flex-grow">
                    {editingProduct ? (
                        <form onSubmit={handleSave} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{isEnglishBeta ? 'Product name' : '商品名'} <span className="text-red-500">*</span></label>
                                <input type="text" required className="w-full p-2 border rounded mt-1" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{isEnglishBeta ? 'Unit price (JPY)' : '単価 (円)'}</label>
                                    <input type="number" className="w-full p-2 border rounded mt-1 text-right" value={form.unit_price} onChange={e => setForm({...form, unit_price: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{isEnglishBeta ? 'Cost (JPY)' : '原価 (円)'}</label>
                                    <input type="number" className="w-full p-2 border rounded mt-1 text-right bg-yellow-50" value={form.cost_price} onChange={e => setForm({...form, cost_price: e.target.value})} />
                                    <p className="text-xs text-gray-500 mt-1">{isEnglishBeta ? 'Used for gross-margin analysis.' : '※粗利分析に使用されます'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{isEnglishBeta ? 'Tax rate (%)' : '税率 (%)'}</label>
                                    <select className="w-full p-2 border rounded mt-1" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: e.target.value})}>
                                        <option value="10">10%</option>
                                        <option value="8">{isEnglishBeta ? '8% (reduced)' : '8% (軽減)'}</option>
                                        <option value="0">{isEnglishBeta ? '0% (tax exempt)' : '0% (非課税)'}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-4">
                                <button type="button" onClick={() => setEditingProduct(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">{isEnglishBeta ? 'Save' : '保存'}</button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <div className="flex justify-end mb-4">
                                <button onClick={() => { setForm({ name: '', unit_price: 0, cost_price: 0, tax_rate: 10 }); setEditingProduct({}); }} className="flex items-center gap-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm">
                                    <PlusIcon /> {isEnglishBeta ? 'Add Product' : '商品追加'}
                                </button>
                            </div>
                            {loading ? <LoadingSpinner /> : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left text-gray-600">
                                        <thead className="bg-gray-100 text-gray-700 uppercase">
                                            <tr>
                                                <th className="px-4 py-2">{isEnglishBeta ? 'Product' : '商品名'}</th>
                                                <th className="px-4 py-2 text-right">{isEnglishBeta ? 'Unit Price' : '単価'}</th>
                                                <th className="px-4 py-2 text-right">{isEnglishBeta ? 'Cost' : '原価'}</th>
                                                <th className="px-4 py-2 text-right">{isEnglishBeta ? 'Gross Margin' : '粗利'}</th>
                                                <th className="px-4 py-2 text-center">{isEnglishBeta ? 'Tax' : '税率'}</th>
                                                <th className="px-4 py-2 w-20"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {products.length === 0 ? (
                                                <tr><td colSpan="6" className="text-center py-4">{isEnglishBeta ? 'No products yet.' : 'データがありません'}</td></tr>
                                            ) : products.map(p => (
                                                <tr key={p.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-2 font-medium">{p.name}</td>
                                                    <td className="px-4 py-2 text-right">¥{Number(p.unit_price).toLocaleString()}</td>
                                                    <td className="px-4 py-2 text-right text-gray-500">¥{Number(p.cost_price).toLocaleString()}</td>
                                                    <td className="px-4 py-2 text-right text-green-600">¥{(Number(p.unit_price) - Number(p.cost_price)).toLocaleString()}</td>
                                                    <td className="px-4 py-2 text-center">{p.tax_rate}%</td>
                                                    <td className="px-4 py-2 flex justify-end gap-2">
                                                        <button onClick={() => { setForm(p); setEditingProduct(p); }} className="text-blue-600 hover:bg-blue-50 p-1 rounded"><EditIcon /></button>
                                                        <button onClick={() => handleDelete(p.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><TrashIcon /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const CONTRACT_CATEGORIES = [
    { id: 'outsourcing', label: '業務委託（請負）', icon: FileTextIcon, desc: 'システム開発、デザイン、制作など' },
    { id: 'quasi_mandate', label: '準委任・コンサル', icon: UserIcon, desc: '月額顧問、アドバイザリー、伴走支援' },
    { id: 'nda', label: '秘密保持契約(NDA)', icon: LockIcon, desc: '商談開始時の情報漏洩防止' },
    { id: 'order_form', label: '注文書・請書', icon: ReceiptIcon, desc: '簡易的な取引合意に' },
    { id: 'amendment', label: '変更覚書', icon: UndoIcon, desc: '既存契約の条件変更・期間延長' },
    { id: 'termination', label: '解約合意書', icon: TrashIcon, desc: 'トラブルを防ぐための円満終了' },
];

// 1. 発行元プロフィール設定モーダル
function IssuerSettingsModal({ onClose, clientId, isEnglishBeta = false }) {
    const [profiles, setProfiles] = useState([]);
    const [editingProfile, setEditingProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchProfiles = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/issuer-profiles?client_id=${clientId}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) setProfiles(data.profiles || []);
        } catch (err) { console.error(err); }
        finally { setIsLoading(false); }
    }, [clientId]);

    useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

    const handleSave = async (e) => {
        e.preventDefault();
        const method = editingProfile.id ? "PUT" : "POST";
        const url = editingProfile.id 
            ? `${API_URL}/api/issuer-profiles/${editingProfile.id}`
            : `${API_URL}/api/issuer-profiles`;
        
        try {
            const res = await fetch(url, {
                method,
                headers: getAuthHeader(true),
                body: JSON.stringify({ ...editingProfile, client_id: clientId })
            });
            if (res.ok) {
                setEditingProfile(null);
                fetchProfiles();
            } else {
                alert(isEnglishBeta ? "Failed to save." : "保存に失敗しました");
            }
        } catch (err) { alert(err.message); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(isEnglishBeta ? "Delete this issuer profile?" : "削除しますか？")) return;
        try {
            await fetch(`${API_URL}/api/issuer-profiles/${id}`, { 
                method: "DELETE", 
                headers: getAuthHeader(true),
                body: JSON.stringify({ client_id: clientId })
            });
            fetchProfiles();
        } catch (err) { alert(err.message); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-3xl rounded-lg shadow-xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="font-bold text-lg">{isEnglishBeta ? 'Invoice Issuer Settings' : '請求書 発行元設定'}</h3>
                    <button onClick={onClose}><CloseIcon /></button>
                </div>
                <div className="p-6 overflow-y-auto flex-grow">
                    {editingProfile ? (
                        <form onSubmit={handleSave} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-bold text-gray-700">{isEnglishBeta ? 'Profile name (e.g. HQ)' : '管理名 (例: 本社)'}</label>
                                    <input type="text" required className="w-full border p-2 rounded" value={editingProfile.profile_name || ''} onChange={e => setEditingProfile({...editingProfile, profile_name: e.target.value})} />
                                </div>
                                <div className="flex items-center pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={editingProfile.is_default} onChange={e => setEditingProfile({...editingProfile, is_default: e.target.checked})} />
                                        <span className="text-sm">{isEnglishBeta ? 'Use as default' : 'デフォルトとして使用'}</span>
                                    </label>
                                </div>
                            </div>
                            <hr />
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-sm font-bold">{isEnglishBeta ? 'Company name' : '会社名'}</label><input type="text" required className="w-full border p-2 rounded" value={editingProfile.company_name || ''} onChange={e => setEditingProfile({...editingProfile, company_name: e.target.value})} /></div>
                                <div><label className="text-sm font-bold">{isEnglishBeta ? 'Representative' : '代表者名'}</label><input type="text" className="w-full border p-2 rounded" value={editingProfile.representative_name || ''} onChange={e => setEditingProfile({...editingProfile, representative_name: e.target.value})} /></div>
                                <div><label className="text-sm font-bold">{isEnglishBeta ? 'Postal code' : '郵便番号'}</label><input type="text" className="w-full border p-2 rounded" value={editingProfile.postal_code || ''} onChange={e => setEditingProfile({...editingProfile, postal_code: e.target.value})} /></div>
                                <div className="col-span-2"><label className="text-sm font-bold">{isEnglishBeta ? 'Address' : '住所'}</label><input type="text" className="w-full border p-2 rounded" value={editingProfile.address || ''} onChange={e => setEditingProfile({...editingProfile, address: e.target.value})} /></div>
                                <div><label className="text-sm font-bold">{isEnglishBeta ? 'Phone' : '電話番号'}</label><input type="text" className="w-full border p-2 rounded" value={editingProfile.phone || ''} onChange={e => setEditingProfile({...editingProfile, phone: e.target.value})} /></div>
                                <div><label className="text-sm font-bold">Email</label><input type="text" className="w-full border p-2 rounded" value={editingProfile.email || ''} onChange={e => setEditingProfile({...editingProfile, email: e.target.value})} /></div>
                            </div>
                            <hr />
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="text-sm font-bold text-blue-700">{isEnglishBeta ? 'Tax registration number' : '登録番号 (インボイス)'}</label><input type="text" placeholder="T1234..." className="w-full border p-2 rounded" value={editingProfile.registration_number || ''} onChange={e => setEditingProfile({...editingProfile, registration_number: e.target.value})} /></div>
                                <div className="col-span-2"><label className="text-sm font-bold text-blue-700">{isEnglishBeta ? 'Bank transfer information' : '振込先情報'}</label><textarea rows="3" className="w-full border p-2 rounded" placeholder={isEnglishBeta ? 'Bank name, branch, account...' : '〇〇銀行 〇〇支店...'} value={editingProfile.bank_info || ''} onChange={e => setEditingProfile({...editingProfile, bank_info: e.target.value})}></textarea></div>
                            </div>
                            <div className="flex justify-end gap-2 pt-4">
                                <button type="button" onClick={() => setEditingProfile(null)} className="px-4 py-2 border rounded">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">{isEnglishBeta ? 'Save' : '保存'}</button>
                            </div>
                        </form>
                    ) : (
                        <div>
                            <div className="flex justify-end mb-4">
                                <button onClick={() => setEditingProfile({ is_default: false })} className="flex items-center gap-1 text-sm bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"><PlusIcon /> {isEnglishBeta ? 'Add Profile' : '新規追加'}</button>
                            </div>
                            {isLoading ? <LoadingSpinner /> : (
                                <ul className="space-y-2">
                                    {profiles.map(p => (
                                        <li key={p.id} className="border p-4 rounded flex justify-between items-center hover:bg-gray-50">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold">{p.profile_name}</span>
                                                    {p.is_default === 1 && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">{isEnglishBeta ? 'Default' : 'デフォルト'}</span>}
                                                </div>
                                                <p className="text-sm text-gray-600">{p.company_name}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => setEditingProfile(p)} className="text-blue-600 p-1 hover:bg-blue-100 rounded"><EditIcon /></button>
                                                <button onClick={() => handleDelete(p.id)} className="text-red-600 p-1 hover:bg-red-100 rounded"><TrashIcon /></button>
                                            </div>
                                        </li>
                                    ))}
                                    {profiles.length === 0 && <p className="text-center text-gray-500">{isEnglishBeta ? 'No issuer profiles yet.' : '登録がありません。'}</p>}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// 契約書雛形管理モーダル（プレビュー・スケーリング・サンプル置換機能付き）
function ContractTemplateModal({ onClose, clientId }) {
    const [templates, setTemplates] = useState([]);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [promptText, setPromptText] = useState("");
    const [isCopied, setIsCopied] = useState(false);

    // --- 🚀 プレビュー用スケーリング管理 ---
    const [previewScale, setPreviewScale] = useState(0.4);
    const previewContainerRef = useRef(null);

    // プレビュー時にタグを置き換えるためのサンプルデータ
    const DUMMY_VARIABLES = {
        company_name: "株式会社サンプルエンドユーザー",
        address: "東京都新宿区西新宿1-1-1",
        representative: "山田 太郎",
        date: new Date().toLocaleDateString('ja-JP'),
        amount: "1,100,000円(税込)",
        contract_period: "2026年4月1日〜2027年3月31日",
        item_name: "Sales Breaker OEM導入・運用支援一式"
    };

    // 発行元情報を取得（プロンプト用）
    const [myProfile, setMyProfile] = useState(null);

    const updatePreviewScale = useCallback(() => {
        if (!previewContainerRef.current) return;
        const container = previewContainerRef.current;
        const availableWidth = container.offsetWidth - 60;
        const availableHeight = container.offsetHeight - 60;
        const scale = Math.min(availableWidth / 793, availableHeight / 1122, 1);
        setPreviewScale(scale);
    }, []);

    useEffect(() => {
        if (editingTemplate) {
            updatePreviewScale();
            window.addEventListener('resize', updatePreviewScale);
        }
        return () => window.removeEventListener('resize', updatePreviewScale);
    }, [editingTemplate, updatePreviewScale]);

    const fetchTemplates = useCallback(async () => {
        setIsLoading(true);
        try {
            const [tRes, pRes] = await Promise.all([
                fetch(`${API_URL}/api/contracts/templates?client_id=${clientId}`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/api/issuer-profiles?client_id=${clientId}`, { headers: getAuthHeader() })
            ]);
            const tData = await tRes.json();
            const pData = await pRes.json();
            if (tData.success) setTemplates(tData.templates || []);
            if (pData.success) setMyProfile(pData.profiles.find(p => p.is_default) || pData.profiles[0]);
        } catch (e) { console.error(e); }
        finally { setIsLoading(false); }
    }, [clientId]);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    // HTML内の {{tag}} をサンプルテキストに置換して表示用HTMLを作る
    const getPreviewHtml = (rawHtml) => {
        if (!rawHtml) return "";
        let html = rawHtml;
        Object.entries(DUMMY_VARIABLES).forEach(([key, val]) => {
            const regex = new RegExp(`{{${key}}}`, 'g');
            // プレビューだと分かりやすいように少し色をつける
            html = html.replace(regex, `<span style="color: #2563eb; font-weight: bold; background: #eff6ff; padding: 0 2px; border-radius: 2px;">${val}</span>`);
        });
        return html;
    };

    // 🚀 軍師プロンプト生成
    const generateContractPrompt = () => {
        const cat = CONTRACT_CATEGORIES.find(c => c.id === editingTemplate.category) || CONTRACT_CATEGORIES[0];
        
        const text = `
あなたは契約法務に精通したエンジニアです。
以下の条件に基づき、B2B向けの「${cat.label}」のHTMLコードを生成してください。

# 1. あなた（甲 / 発行元）の情報
- 会社名: ${myProfile?.company_name || "（未設定）"}
- 住所: ${myProfile?.address || "（未設定）"}
- 代表者: ${myProfile?.representative_name || "（未設定）"}

# 2. 契約の個別要望（箇条書き）
${editingTemplate.requirements || "特になし"}

# 3. 制作ルール（厳守）
- A4縦サイズ(793x1122)を想定したHTML/CSSにすること。
- 背景は白、文字色は黒、フォントは明朝体またはゴシック。
- 変数タグを適切な位置に配置してください: 
  {{company_name}} (相手の社名), {{address}} (相手の住所), {{representative}} (相手の代表), {{date}}, {{amount}}, {{contract_period}}, {{item_name}}
- AIが書いたような前置きは一切不要です。HTMLコードのみ出力してください。
`.trim();
        setPromptText(text);
    };

    const handleCopyAndOpenGem = () => {
        navigator.clipboard.writeText(promptText);
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
            window.open("https://gemini.google.com/gem/1jA58_DqoYvIW6qbumskdQqM1ZOKabhYB?usp=sharing", '_blank');
        }, 1500);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_URL}/api/contracts/templates`, {
                method: "POST",
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ ...editingTemplate, client_id: clientId })
            });
            if (res.ok) { setEditingTemplate(null); fetchTemplates(); }
        } catch (e) { alert("保存失敗"); }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("この雛形を削除しますか？")) return;
        try {
            const res = await fetch(`${API_URL}/api/contracts/templates/${id}`, {
                method: "DELETE",
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId })
            });
            if (res.ok) { setEditingTemplate(null); fetchTemplates(); }
        } catch (e) { alert("削除失敗"); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-md">
            <div className="bg-white w-full max-w-[98vw] h-[95vh] rounded-[3rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                
                {/* --- Header --- */}
                <div className="p-6 border-b flex justify-between items-center bg-white">
                    <div>
                        <h3 className="text-xl font-black flex items-center gap-3"><FileTextIcon className="text-indigo-500" /> 契約書雛形エンジン</h3>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Contract Template Master Management</p>
                    </div>
                    <button onClick={onClose} className="text-slate-300 hover:text-slate-600 text-5xl transition-colors">&times;</button>
                </div>

                <div className="flex-grow flex overflow-hidden">
                    
                    {/* --- 1. 左サイド：一覧 ＆ プロンプト設定（幅 320px） --- */}
                    <div className="w-80 border-r bg-slate-50/50 flex flex-col overflow-hidden">
                        <div className="p-6 overflow-y-auto flex-grow space-y-6 scrollbar-hide">
                            <button 
                                onClick={() => setEditingTemplate({ title: '', html_content: '', category: 'outsourcing', requirements: '' })} 
                                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                            >
                                <PlusIcon size={18}/> 新規雛形作成
                            </button>
                            
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">保存済みリスト</label>
                                {templates.map(t => (
                                    <div key={t.id} className="group relative">
                                        <div 
                                            onClick={() => setEditingTemplate(t)} 
                                            className={`p-4 rounded-2xl cursor-pointer transition-all border-2 ${editingTemplate?.id === t.id ? 'bg-white border-indigo-500 shadow-md' : 'border-transparent hover:bg-white'}`}
                                        >
                                            <p className="text-xs font-black text-slate-700 truncate pr-6">{t.title || "(無題)"}</p>
                                            <p className="text-[9px] text-slate-400 mt-1 font-bold uppercase">{t.category}</p>
                                        </div>
                                        <button onClick={() => handleDelete(t.id)} className="absolute right-3 top-4 opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all">
                                            <TrashIcon size={14}/>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {editingTemplate && (
                                <div className="pt-6 border-t space-y-6">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><LockIcon size={10}/> 契約の種類</label>
                                        <div className="grid grid-cols-1 gap-2">
                                            {CONTRACT_CATEGORIES.map(cat => (
                                                <div 
                                                    key={cat.id} 
                                                    onClick={() => setEditingTemplate({...editingTemplate, category: cat.id})} 
                                                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-3 ${editingTemplate.category === cat.id ? 'border-indigo-500 bg-white shadow-sm' : 'border-slate-100 hover:border-indigo-200'}`}
                                                >
                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${editingTemplate.category === cat.id ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-500'}`}><cat.icon size={14}/></div>
                                                    <span className="text-[11px] font-black">{cat.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><WandIcon size={10}/> 具体的要望</label>
                                        <textarea 
                                            className="w-full h-32 p-4 bg-white border-2 border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:border-indigo-500 resize-none shadow-inner"
                                            placeholder="例：OEM契約。報酬はレベニューシェア。契約期間は自動更新。"
                                            value={editingTemplate.requirements}
                                            onChange={e => setEditingTemplate({...editingTemplate, requirements: e.target.value})}
                                        />
                                        <button onClick={generateContractPrompt} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs hover:bg-black transition-all flex items-center justify-center gap-2">
                                            生成する
                                        </button>
                                    </div>

                                    {promptText && (
                                        <div className="space-y-3 animate-in slide-in-from-left-4">
                                            <textarea readOnly className="w-full h-32 p-4 bg-indigo-950 text-indigo-200 font-mono text-[9px] rounded-2xl outline-none" value={promptText} />
                                            <button onClick={handleCopyAndOpenGem} className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-100">
                                                {isCopied ? "コピー完了！" : "コピーしてGeminiを開く"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* --- 2. 中央：エディタ（幅 450px） --- */}
                    {editingTemplate ? (
                        <>
                        <div className="w-[450px] flex flex-col bg-white border-r">
                            <div className="p-6 border-b space-y-4">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">雛形名称</label>
                                <input 
                                    type="text" 
                                    className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl font-black text-sm focus:border-indigo-500 focus:bg-white transition-all outline-none" 
                                    placeholder="例：【公式】標準業務委託契約書" 
                                    value={editingTemplate.title} 
                                    onChange={e => setEditingTemplate({...editingTemplate, title: e.target.value})} 
                                />
                            </div>
                            <div className="flex-grow flex flex-col">
                                <div className="px-6 py-2 bg-slate-800 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                                    HTML / CSS Editor
                                </div>
                                <textarea 
                                    className="flex-grow w-full p-6 font-mono text-[10px] bg-slate-900 text-green-400 outline-none resize-none leading-relaxed"
                                    value={editingTemplate.html_content}
                                    onChange={e => setEditingTemplate({...editingTemplate, html_content: e.target.value})}
                                    placeholder="Geminiから出力されたHTMLコードをここにペーストしてください..."
                                />
                            </div>
                            <div className="p-6 bg-slate-50 border-t">
                                <button 
                                    onClick={handleSave} 
                                    disabled={!editingTemplate.title || !editingTemplate.html_content}
                                    className="w-full py-5 bg-indigo-600 text-white rounded-full font-black text-sm shadow-2xl shadow-indigo-100 hover:bg-indigo-700 disabled:bg-slate-200 transition-all"
                                >
                                    この雛形を保存する
                                </button>
                            </div>
                        </div>

                        {/* --- 3. 右サイド：A4プレビュー領域 --- */}
                        <div ref={previewContainerRef} className="flex-grow bg-slate-200 flex items-center justify-center relative overflow-hidden group">
                            {/* 拡大率表示デコレーション */}
                            <div className="absolute top-6 left-8 z-10 bg-white/90 backdrop-blur px-5 py-2 rounded-full text-[10px] font-black text-slate-600 shadow-xl flex items-center gap-3 border border-slate-100">
                                <EyeIcon size={14} className="text-indigo-500"/>
                                A4 PREVIEW {Math.round(previewScale * 100)}%
                            </div>
                            
                            {/* A4キャンバス */}
                            <div style={{ 
                                width: '793px', 
                                height: '1122px', 
                                transform: `scale(${previewScale})`, 
                                transformOrigin: 'center center',
                                flexShrink: 0
                            }} className="bg-white shadow-[0_30px_100px_rgba(0,0,0,0.2)] relative transition-transform duration-500">
                                <iframe 
                                    className="w-full h-full border-none" 
                                    srcDoc={`
                                        <html>
                                            <head>
                                                <script src="https://cdn.tailwindcss.com"></script>
                                                <style>
                                                    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;700&display=swap');
                                                    body { 
                                                        margin: 0; 
                                                        padding: 50px; 
                                                        font-family: 'Noto Serif JP', serif;
                                                        background: white;
                                                        color: #1a1a1a;
                                                        line-height: 1.7;
                                                    }
                                                    /* プレビュー時の特殊スタイル */
                                                    * { box-sizing: border-box; }
                                                </style>
                                            </head>
                                            <body>
                                                ${getPreviewHtml(editingTemplate.html_content)}
                                            </body>
                                        </html>
                                    `} 
                                    title="Contract Template Preview" 
                                />
                                {!editingTemplate.html_content && (
                                    <div className="absolute inset-0 flex items-center justify-center text-slate-100 font-black text-6xl uppercase tracking-tighter -rotate-12 pointer-events-none">
                                        No Content
                                    </div>
                                )}
                            </div>
                        </div>
                        </>
                    ) : (
                        <div className="flex-grow flex flex-col items-center justify-center bg-slate-50">
                            <div className="w-24 h-24 bg-white rounded-3xl shadow-xl flex items-center justify-center text-slate-200 mb-6">
                                <FileTextIcon size={48} />
                            </div>
                            <p className="font-black text-slate-300 uppercase tracking-[0.3em] text-sm animate-pulse">Select or Create a Template</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// 2. 請求書作成モーダル (★修正: 商品マスタからの読み込み追加)
function InvoiceFormModal({ customer, clientId, onClose, onSuccess, type = 'invoice', users, sourceInvoice, isEnglishBeta = false }) {
    const [profiles, setProfiles] = useState([]);
    const [billingProducts, setBillingProducts] = useState([]); // ★ 追加
    const [formData, setFormData] = useState({
        issuer_profile_id: "",
        issue_date: new Date().toISOString().split('T')[0],
        due_date: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
        items: [{ name: "", quantity: 1, unit_price: 0, tax_rate: 10, cost_price: 0 }],
        notes: isEnglishBeta ? "Bank transfer fees are the customer's responsibility." : "※振込手数料は貴社負担でお願いいたします。",
        // 🚀 追加項目
        allowed_payment_methods: 'bank_transfer', // 初期値は振込のみ
        issuer_bank_info: "" // プロフィールから読み込む用
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 初期データロード
    useEffect(() => {
        // プロフィール取得
        fetch(`${API_URL}/api/issuer-profiles?client_id=${clientId}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.profiles.length > 0) {
                    setProfiles(data.profiles);
                    const def = data.profiles.find(p => p.is_default) || data.profiles[0];
                    
                    if (sourceInvoice) {
                        let items = [];
                        try {
                            items = typeof sourceInvoice.items_json === 'string' 
                                ? JSON.parse(sourceInvoice.items_json) 
                                : sourceInvoice.items_json || [{ name: "", quantity: 1, unit_price: 0, tax_rate: 10, cost_price: 0 }];
                        } catch(e) { console.error("Parse Error", e); }

                        setFormData({
                            issuer_profile_id: def.id,
                            issue_date: new Date().toISOString().split('T')[0],
                            due_date: "", 
                            items: items,
                            notes: type === 'receipt' ? (isEnglishBeta ? "Received the above amount." : "上記正に領収いたしました。") : sourceInvoice.notes
                        });
                    } else {
                        setFormData(prev => ({ 
                            ...prev, 
                            issuer_profile_id: def.id,
                            issuer_bank_info: def.bank_info || "" // 振込先をセット
                        }));
                    }
                }
            });
        
        // ★ 追加: 商品マスタ取得
        fetch(`${API_URL}/api/crm/billing-products?client_id=${clientId}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => {
                if (data.success) setBillingProducts(data.products || []);
            });

    }, [clientId, sourceInvoice, type, isEnglishBeta]);

    const handleItemChange = (idx, field, val) => {
        const newItems = [...formData.items];
        newItems[idx][field] = val;
        setFormData({ ...formData, items: newItems });
    };

    // ★ 追加: 商品マスタから選択時の処理
    const handleProductSelect = (idx, productId) => {
        const product = billingProducts.find(p => p.id === parseInt(productId));
        if (product) {
            const newItems = [...formData.items];
            newItems[idx] = {
                ...newItems[idx],
                name: product.name,
                unit_price: product.unit_price,
                cost_price: product.cost_price, // 原価もセット
                tax_rate: product.tax_rate
            };
            setFormData({ ...formData, items: newItems });
        }
    };

    const addItem = () => setFormData({ ...formData, items: [...formData.items, { name: "", quantity: 1, unit_price: 0, tax_rate: 10, cost_price: 0 }] });
    const removeItem = (idx) => setFormData({ ...formData, items: formData.items.filter((_, i) => i !== idx) });

    const calculateTotal = () => {
        let sub = 0, tax = 0;
        formData.items.forEach(item => {
            const amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
            sub += amount;
            tax += Math.floor(amount * (parseInt(item.tax_rate) / 100));
        });
        return { sub, tax, total: sub + tax };
    };
    const totals = calculateTotal();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                client_id: clientId,
                customer_id: customer.id,
                type: type,
                ...formData,
            };
            const res = await fetch(`${API_URL}/api/crm/invoices`, {
                method: "POST",
                headers: getAuthHeader(true),
                body: JSON.stringify(payload)
            });
            
            const data = await res.json();
            if (data.success) {
                onSuccess(data);
            } else {
                alert((isEnglishBeta ? "Error: " : "エラー: ") + data.error);
            }
        } catch (err) { alert(err.message); }
        finally { setIsSubmitting(false); }
    };

    const titleMap = isEnglishBeta
        ? { invoice: 'Invoice', quotation: 'Quote', receipt: 'Receipt' }
        : { invoice: '請求書', quotation: '見積書', receipt: '領収書' };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="font-bold text-lg">{isEnglishBeta ? `Create ${titleMap[type]}` : `${titleMap[type]} 作成`}</h3>
                    <button onClick={onClose}><CloseIcon /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-grow space-y-6">
                    {profiles.length === 0 && (
                        <div className="bg-yellow-50 p-3 text-yellow-800 text-sm rounded">
                            {isEnglishBeta ? 'No issuer profile is set. Add one from Billing Settings first.' : '⚠ 発行元プロフィールが設定されていません。右上の設定ボタンから登録してください。'}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold mb-1">{isEnglishBeta ? 'Issuer profile' : '発行元プロフィール'}</label>
                            <select 
                                className="w-full border p-2 rounded" 
                                value={formData.issuer_profile_id} 
                                onChange={e => {
                                    const profileId = e.target.value;
                                    const selected = profiles.find(p => p.id === parseInt(profileId));
                                    setFormData({
                                        ...formData, 
                                        issuer_profile_id: profileId,
                                        issuer_bank_info: selected?.bank_info || "" // 🚀 振込先を連動させる
                                    });
                                }} 
                                required
                            >
                                {profiles.map(p => <option key={p.id} value={p.id}>{p.profile_name}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold mb-1">{isEnglishBeta ? 'Issue date' : '発行日'}</label>
                                <input type="date" className="w-full border p-2 rounded" value={formData.issue_date} onChange={e => setFormData({...formData, issue_date: e.target.value})} required />
                            </div>
                            {type === 'invoice' && (
                                <div>
                                    <label className="block text-sm font-bold mb-1">{isEnglishBeta ? 'Due date' : '支払期限'}</label>
                                    <input type="date" className="w-full border p-2 rounded" value={formData.due_date} onChange={e => setFormData({...formData, due_date: e.target.value})} required />
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold mb-2">{isEnglishBeta ? 'Line items' : '明細'}</label>
                        <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="p-2 text-left w-48">{isEnglishBeta ? 'Item (master optional)' : '品目 (マスタ選択可)'}</th>
                                    <th className="p-2 w-20">{isEnglishBeta ? 'Qty' : '数量'}</th>
                                    <th className="p-2 w-32">{isEnglishBeta ? 'Unit Price' : '単価'}</th>
                                    <th className="p-2 w-24">{isEnglishBeta ? 'Tax' : '税率'}</th>
                                    <th className="p-2 w-32 text-right">{isEnglishBeta ? 'Amount' : '金額'}</th>
                                    <th className="p-2 w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.items.map((item, idx) => (
                                    <tr key={idx} className="border-b">
                                        <td className="p-1">
                                            {/* ★ 修正: マスタ選択と自由入力を併用 */}
                                            <div className="flex flex-col gap-1">
                                                <select 
                                                    className="w-full border p-1 rounded text-xs bg-blue-50 mb-1" 
                                                    onChange={(e) => handleProductSelect(idx, e.target.value)}
                                                    defaultValue=""
                                                >
                                                    <option value="" disabled>{isEnglishBeta ? 'Select from master...' : 'マスタから選択...'}</option>
                                                    {billingProducts.map(p => (
                                                        <option key={p.id} value={p.id}>{p.name}</option>
                                                    ))}
                                                </select>
                                                <input type="text" className="w-full border p-1 rounded" value={item.name} onChange={e => handleItemChange(idx, 'name', e.target.value)} required />
                                            </div>
                                        </td>
                                        <td className="p-1 align-top"><input type="number" className="w-full border p-1 rounded" value={item.quantity} onChange={e => handleItemChange(idx, 'quantity', e.target.value)} min="0" step="0.1" required /></td>
                                        <td className="p-1 align-top"><input type="number" className="w-full border p-1 rounded" value={item.unit_price} onChange={e => handleItemChange(idx, 'unit_price', e.target.value)} min="0" required /></td>
                                        <td className="p-1 align-top">
                                            <select className="w-full border p-1 rounded" value={item.tax_rate} onChange={e => handleItemChange(idx, 'tax_rate', e.target.value)}>
                                                <option value="10">10%</option>
                                                <option value="8">8%</option>
                                            </select>
                                        </td>
                                        <td className="p-1 align-top text-right font-mono pt-2">
                                            {Math.floor((item.quantity * item.unit_price)).toLocaleString()}
                                        </td>
                                        <td className="p-1 align-top text-center">
                                            <button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:bg-red-100 p-1 rounded"><TrashIcon /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button type="button" onClick={addItem} className="mt-2 text-blue-600 text-sm flex items-center gap-1"><PlusIcon /> {isEnglishBeta ? 'Add line item' : '明細を追加'}</button>
                    </div>

                    <div className="flex justify-end">
                        <div className="w-1/3 bg-gray-50 p-4 rounded">
                            <div className="flex justify-between mb-1"><span>{isEnglishBeta ? 'Subtotal' : '小計'}</span><span>¥{totals.sub.toLocaleString()}</span></div>
                            <div className="flex justify-between mb-1 text-sm text-gray-500"><span>{isEnglishBeta ? 'Tax' : '消費税'}</span><span>¥{totals.tax.toLocaleString()}</span></div>
                            <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2"><span>{isEnglishBeta ? 'Total' : '合計'}</span><span>¥{totals.total.toLocaleString()}</span></div>
                        </div>
                    </div>

                    {/* --- 🚀 [修正箇所]: 決済・振込先・備考設定エリア --- */}
                    <div className="space-y-4 pt-4 border-t">
                        <div className="flex items-center justify-between bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                            <div>
                                <h4 className="text-sm font-bold text-indigo-900 flex items-center gap-2">
                                    <CreditCardIcon /> {isEnglishBeta ? 'Allow credit card payment' : 'クレジットカード決済を許可'}
                                </h4>
                                <p className="text-[10px] text-indigo-600 mt-1">
                                    {isEnglishBeta ? 'When enabled, the customer can pay this invoice by card from the invoice page.' : '有効にすると、顧客が請求書画面からカードで即時支払い可能になります。'}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    className="sr-only peer"
                                    checked={formData.allowed_payment_methods.includes('card')}
                                    onChange={(e) => {
                                        const methods = e.target.checked ? 'card,bank_transfer' : 'bank_transfer';
                                        setFormData({...formData, allowed_payment_methods: methods});
                                    }}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                            </label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">{isEnglishBeta ? 'Bank transfer information (snapshot)' : '振込先情報 (スナップショット)'}</label>
                                <textarea 
                                    className="w-full border p-2 rounded text-sm bg-gray-50 focus:bg-white transition-colors" 
                                    rows="3" 
                                    placeholder={isEnglishBeta ? 'Bank name, branch, account...' : '〇〇銀行 〇〇支店...'}
                                    value={formData.issuer_bank_info} 
                                    onChange={e => setFormData({...formData, issuer_bank_info: e.target.value})}
                                ></textarea>
                                <p className="text-[10px] text-gray-400 mt-1">{isEnglishBeta ? 'This snapshot is printed only on this document. The default comes from the issuer profile.' : '※この請求書にのみ印字される情報です。発行元設定から初期値が反映されています。'}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">{isEnglishBeta ? 'Notes' : '備考'}</label>
                                <textarea 
                                    className="w-full border p-2 rounded text-sm" 
                                    rows="3" 
                                    value={formData.notes} 
                                    onChange={e => setFormData({...formData, notes: e.target.value})}
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 border rounded">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-blue-600 text-white rounded font-bold disabled:bg-gray-400">{isEnglishBeta ? 'Issue' : '発行する'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// 3. 新規顧客登録モーダル (★ 修正: 担当者選択を追加)
function NewCustomerModal({ clientId, onClose, onSuccess, users, isEnglishBeta = false }) {
    const [form, setForm] = useState({
        company_name: "", representative_name: "", phone: "",
        postal_code: "", address: "",
        registration_number: "", payment_terms: "",
        notes: "",
        contract_type: "spot", contract_amount: "", contract_status: "active",
        contract_start_date: new Date().toISOString().split('T')[0],
        contract_end_date: "",
        acquisition_source: "",
        tags: "",
        next_action_date: "", next_action_note: "",
        assigned_user_id: "" // ★ 追加
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!form.company_name) return alert(isEnglishBeta ? "Company name is required." : "会社名は必須です");
        
        setIsSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/crm/customers`, {
                method: "POST",
                headers: getAuthHeader(true),
                body: JSON.stringify({ ...form, client_id: clientId })
            });
            const data = await res.json();
            if (data.success) {
                onSuccess(data);
            } else {
                alert((isEnglishBeta ? "Error: " : "エラー: ") + data.error);
            }
        } catch (err) { alert(err.message); }
        finally { setIsSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-3xl rounded-lg shadow-xl flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="font-bold text-lg">{isEnglishBeta ? 'New Customer' : '新規顧客登録'}</h3>
                    <button onClick={onClose}><CloseIcon /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-grow space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2"><label className="text-sm font-bold">{isEnglishBeta ? 'Company name' : '会社名'} <span className="text-red-500">*</span></label><input className="w-full border p-2 rounded" required value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} /></div>
                        <div><label className="text-sm font-bold">{isEnglishBeta ? 'Representative' : '代表者名'}</label><input className="w-full border p-2 rounded" value={form.representative_name} onChange={e => setForm({...form, representative_name: e.target.value})} /></div>
                        
                        {/* ★ 追加: 担当者選択 */}
                        <div>
                            <label className="text-sm font-bold">{isEnglishBeta ? 'Internal owner' : '社内担当者'}</label>
                            <select 
                                className="w-full border p-2 rounded bg-white" 
                                value={form.assigned_user_id} 
                                onChange={e => setForm({...form, assigned_user_id: e.target.value})}
                            >
                                <option value="">{isEnglishBeta ? 'Unassigned' : '未割り当て'}</option>
                                {users && users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </div>

                        <div><label className="text-sm font-bold">{isEnglishBeta ? 'Phone' : '電話番号'}</label><input className="w-full border p-2 rounded" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
                        <div><label className="text-sm font-bold">{isEnglishBeta ? 'Postal code' : '郵便番号'}</label><input className="w-full border p-2 rounded" value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} /></div>
                        <div className="col-span-2"><label className="text-sm font-bold">{isEnglishBeta ? 'Address' : '住所'}</label><input className="w-full border p-2 rounded" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
                        <div><label className="text-sm font-bold">{isEnglishBeta ? 'Tax registration number' : 'インボイス登録番号'}</label><input className="w-full border p-2 rounded" value={form.registration_number} onChange={e => setForm({...form, registration_number: e.target.value})} placeholder="T1234..." /></div>
                        <div><label className="text-sm font-bold">{isEnglishBeta ? 'Payment terms' : '支払条件'}</label><input className="w-full border p-2 rounded" value={form.payment_terms} onChange={e => setForm({...form, payment_terms: e.target.value})} placeholder={isEnglishBeta ? 'End-of-month close, due next month' : '月末締め翌月末払い'} /></div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded border">
                        <h4 className="font-bold mb-3 text-gray-700">{isEnglishBeta ? 'Initial Contract' : '初回契約情報'}</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Contract type' : '契約形態'}</label>
                                <select className="w-full border p-2 rounded bg-white" value={form.contract_type} onChange={e => setForm({...form, contract_type: e.target.value})}>
                                    <option value="spot">{getContractTypeLabel('spot', isEnglishBeta)}</option>
                                    <option value="subscription">{getContractTypeLabel('subscription', isEnglishBeta)}</option>
                                    <option value="consulting">{getContractTypeLabel('consulting', isEnglishBeta)}</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Contract amount' : '契約金額'}</label>
                                <input type="number" className="w-full border p-2 rounded" value={form.contract_amount} onChange={e => setForm({...form, contract_amount: e.target.value})} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Start date' : '契約開始日'}</label>
                                <input type="date" className="w-full border p-2 rounded" value={form.contract_start_date} onChange={e => setForm({...form, contract_start_date: e.target.value})} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'End / renewal date' : '終了日/更新日'}</label>
                                <input type="date" className="w-full border p-2 rounded" value={form.contract_end_date} onChange={e => setForm({...form, contract_end_date: e.target.value})} />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-bold">{isEnglishBeta ? 'Acquisition source' : '獲得経路'}</label>
                            <select className="w-full border p-2 rounded bg-white" value={form.acquisition_source} onChange={e => setForm({...form, acquisition_source: e.target.value})}>
                                <option value="">{isEnglishBeta ? 'Not selected' : '未選択'}</option>
                                {Object.entries(ACQUISITION_SOURCE_MAP).map(([key, val]) => (
                                    <option key={key} value={key}>{getAcquisitionSourceLabel(key, isEnglishBeta)}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-bold">{isEnglishBeta ? 'Tags (comma separated)' : 'タグ (カンマ区切り)'}</label>
                            <input 
                                className="w-full border p-2 rounded" 
                                placeholder={isEnglishBeta ? 'e.g. existing customer, important, newsletter' : '例: 既存客, 重要, メルマガ希望'} 
                                value={form.tags} 
                                onChange={e => setForm({...form, tags: e.target.value})} 
                            />
                        </div>
                        <div>
                            <label className="text-sm font-bold">{isEnglishBeta ? 'Next action date' : '次回アクション日'}</label>
                            <input type="date" className="w-full border p-2 rounded" value={form.next_action_date} onChange={e => setForm({...form, next_action_date: e.target.value})} />
                        </div>
                        <div className="col-span-2">
                            <label className="text-sm font-bold">{isEnglishBeta ? 'Next action memo' : '次回アクションメモ'}</label>
                            <input className="w-full border p-2 rounded" placeholder={isEnglishBeta ? 'e.g. Call again at 2 PM' : '例: 14時に再架電'} value={form.next_action_note} onChange={e => setForm({...form, next_action_note: e.target.value})} />
                        </div>
                    </div>

                    <div><label className="text-sm font-bold">{isEnglishBeta ? 'Notes' : '備考'}</label><textarea className="w-full border p-2 rounded" rows="3" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
                    
                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 border rounded">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-blue-600 text-white rounded font-bold disabled:bg-gray-400">{isEnglishBeta ? 'Create' : '登録する'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// 4. 顧客詳細モーダル (メイン)
function CustomerDetailModal({ customerId, clientId, onClose, users, user, isEnglishBeta = false }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("info"); // info, contract, contacts, invoices, timeline
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceType, setInvoiceType] = useState('invoice');
    const [sourceInvoice, setSourceInvoice] = useState(null); 
    const [invoiceFilter, setInvoiceFilter] = useState('all');

    // 編集用State (基本情報)
    const [editInfo, setEditInfo] = useState({});
    
    // 契約管理用State (複数契約対応)
    const [contracts, setContracts] = useState([]); 
    const [editingContract, setEditingContract] = useState(null); 
    const [billingSubscriptions, setBillingSubscriptions] = useState([]);
    const [subscriptionDraft, setSubscriptionDraft] = useState(null);
    const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
    const initialContractForm = { contract_type: 'spot', contract_amount: 0, contract_start_date: '', contract_end_date: '', contract_status: 'active' };

    // 担当者管理用State (編集対応)
    const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', department: '', position: '', role: '' });
    const [editingContactId, setEditingContactId] = useState(null); 

    // タイムライン用State
    const [timelineSummary, setTimelineSummary] = useState(""); 
    const [timelineNote, setTimelineNote] = useState("");
    const [timelineStaff, setTimelineStaff] = useState("");
    const [editingTimelineId, setEditingTimelineId] = useState(null);
    const [editTimelineData, setEditTimelineData] = useState({ summary: "", details: "" });

    // --- 以下のステータスを追記 ---
    const [billingProducts, setBillingProducts] = useState([]); // エラー解決用
    const [contractTemplates, setContractTemplates] = useState([]);
    const [customerDocs, setCustomerDocs] = useState([]);
    const [selectedTplId, setSelectedTplId] = useState("");
    const [selectedProdIds, setSelectedProdIds] = useState([]);
    const [contractVariables, setContractVariables] = useState({ 
        date: new Date().toISOString().split('T')[0], 
        amount: "", 
        period: "",
        manual_item_name: "",
        manual_amount: ""
    });
    const [isGenerating, setIsGenerating] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/crm/customers/${customerId}?client_id=${clientId}&include_contacts=true&include_invoices=true&include_timeline=true`, {
                headers: getAuthHeader()
            });
            const json = await res.json();
            if (json.success) {
                setData(json);
                setEditInfo(json.customer);
                setContracts(json.contracts || []); 
                setBillingSubscriptions(json.billing_subscriptions || []);
                
                // ★ 追加：契約書タブ用の追加データを取得
                if (activeTab === 'contracts_doc') {
                    const [tplRes, prodRes, docRes] = await Promise.all([
                        fetch(`${API_URL}/api/contracts/templates?client_id=${clientId}`, { headers: getAuthHeader() }),
                        fetch(`${API_URL}/api/crm/billing-products?client_id=${clientId}`, { headers: getAuthHeader() }),
                        fetch(`${API_URL}/api/crm/contracts/documents?client_id=${clientId}&customer_id=${customerId}`, { headers: getAuthHeader() })
                    ]);
                    const tData = await tplRes.json();
                    const pData = await prodRes.json();
                    const dData = await docRes.json();
                    if (tData.success) setContractTemplates(tData.templates);
                    if (pData.success) setBillingProducts(pData.products);
                    if (dData.success) setCustomerDocs(dData.documents || []);
                }
            }
        } catch (err) { console.error(err); }
        finally { setIsLoading(false); }
    }, [customerId, clientId, activeTab]); // activeTabを監視対象に追加

    useEffect(() => { fetchData(); }, [fetchData]);

    // 基本情報保存
    const handleSaveInfo = async () => {
        try {
            await fetch(`${API_URL}/api/crm/customers/${customerId}`, {
                method: "PUT",
                headers: getAuthHeader(true),
                body: JSON.stringify({ ...editInfo, client_id: clientId })
            });
            alert(isEnglishBeta ? "Saved." : "保存しました");
            fetchData();
        } catch (err) { alert(err.message); }
    };

    // 履歴保存処理
    const handleSaveTimeline = async () => {
        if (!timelineSummary || !timelineNote) return alert(isEnglishBeta ? "Enter both a title and details." : "件名と内容を入力してください");
        try {
            const res = await fetch(`${API_URL}/api/crm/timeline`, {
                method: "POST",
                headers: getAuthHeader(true),
                body: JSON.stringify({ 
                    client_id: clientId, 
                    customer_id: customerId,
                    activity_type: 'note',
                    summary: timelineSummary,
                    details: timelineNote,
                    performed_by_user_id: timelineStaff || user.id 
                })
            });
            
            if (!res.ok) throw new Error(isEnglishBeta ? "API error" : "APIエラー");
            
            setTimelineSummary(""); 
            setTimelineNote("");
            setTimelineStaff("");
            fetchData(); 
        } catch (err) { alert(isEnglishBeta ? "Failed to save the activity." : "履歴の保存に失敗しました"); }
    };

    // 履歴更新処理
    const handleUpdateTimeline = async () => {
        if (!editTimelineData.summary || !editTimelineData.details) return alert(isEnglishBeta ? "Enter both a title and details." : "件名と内容を入力してください");
        try {
            const res = await fetch(`${API_URL}/api/crm/timeline/${editingTimelineId}`, {
                method: "PUT",
                headers: getAuthHeader(true),
                body: JSON.stringify({
                    client_id: clientId,
                    summary: editTimelineData.summary,
                    details: editTimelineData.details
                })
            });
            if (!res.ok) throw new Error(isEnglishBeta ? "API error" : "APIエラー");
            
            setEditingTimelineId(null); 
            fetchData();
        } catch (err) { alert(isEnglishBeta ? "Failed to update." : "更新に失敗しました"); }
    };

    // 履歴削除処理
    const handleDeleteTimeline = async (id) => {
        if(!window.confirm(isEnglishBeta ? "Delete this activity record?" : "この記録を削除しますか？")) return;
        try {
            const res = await fetch(`${API_URL}/api/crm/timeline/${id}`, {
                method: "DELETE",
                headers: getAuthHeader(true),
                body: JSON.stringify({ client_id: clientId })
            });
            if (!res.ok) throw new Error(isEnglishBeta ? "API error" : "APIエラー");
            fetchData();
        } catch (err) { alert(isEnglishBeta ? "Failed to delete." : "削除に失敗しました"); }
    };

    // --- 担当者管理ロジック ---
    const handleContactSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingContactId) {
                await fetch(`${API_URL}/api/crm/contacts/${editingContactId}`, { 
                    method: "PUT", 
                    headers: getAuthHeader(true), 
                    body: JSON.stringify({ ...newContact, client_id: clientId, customer_id: customerId }) 
                });
            } else {
                await fetch(`${API_URL}/api/crm/contacts`, { 
                    method: "POST", 
                    headers: getAuthHeader(true), 
                    body: JSON.stringify({ ...newContact, client_id: clientId, customer_id: customerId }) 
                });
            }
            setNewContact({ name: '', email: '', phone: '', department: '', position: '', role: '' });
            setEditingContactId(null);
            fetchData();
        } catch (err) { alert(err.message); }
    };

    const handleEditContactClick = (contact) => {
        setNewContact(contact);
        setEditingContactId(contact.id);
    };

    const handleDeleteContact = async (id) => { 
        if (!window.confirm(isEnglishBeta ? "Delete this contact?" : "削除しますか？")) return; 
        try { 
            await fetch(`${API_URL}/api/crm/contacts/${id}`, { 
                method: "DELETE", 
                headers: getAuthHeader(true), 
                body: JSON.stringify({ client_id: clientId }) 
            }); 
            fetchData(); 
        } catch (err) { alert(err.message); } 
    };

    const handleCancelEditContact = () => {
        setNewContact({ name: '', email: '', phone: '', department: '', position: '', role: '' });
        setEditingContactId(null);
    };

    // --- 契約管理ロジック ---
    const handleContractSubmit = async (e) => {
        e.preventDefault();
        if (!editingContract) return;
        const method = editingContract.id ? "PUT" : "POST";
        const url = editingContract.id 
            ? `${API_URL}/api/crm/contracts/${editingContract.id}`
            : `${API_URL}/api/crm/contracts`;
        
        try {
            await fetch(url, {
                method,
                headers: getAuthHeader(true),
                body: JSON.stringify({ ...editingContract, client_id: clientId, customer_id: customerId })
            });
            setEditingContract(null);
            fetchData();
        } catch (err) { alert(err.message); }
    };

    const handleDeleteContract = async (id) => {
        if (!window.confirm(isEnglishBeta ? "Delete this contract?" : "削除しますか？")) return;
        try {
            await fetch(`${API_URL}/api/crm/contracts/${id}`, {
                method: "DELETE",
                headers: getAuthHeader(true),
                body: JSON.stringify({ client_id: clientId })
            });
            fetchData();
        } catch (err) { alert(err.message); }
    };

    const openSubscriptionDraft = (contract) => {
        const amount = Math.max(0, Math.round(Number(contract.contract_amount) || 0));
        setSubscriptionDraft({
            contract_id: contract.id,
            name: `${data?.customer?.company_name || (isEnglishBeta ? 'Customer' : '顧客')} ${isEnglishBeta ? 'subscription' : 'サブスクリプション'}`,
            amount,
            interval_unit: 'month',
            interval_count: 1,
            trial_days: 0
        });
    };

    const handleCreateSubscriptionLink = async (e) => {
        e.preventDefault();
        if (!subscriptionDraft) return;
        setIsCreatingSubscription(true);
        try {
            const res = await fetch(`${API_URL}/api/crm/customer-subscriptions`, {
                method: "POST",
                headers: getAuthHeader(true),
                body: JSON.stringify({
                    client_id: clientId,
                    customer_id: customerId,
                    ...subscriptionDraft
                })
            });
            const result = await res.json();
            if (!res.ok || !result.success) {
                throw new Error(result.error || (isEnglishBeta ? "Failed to create the subscription signup link." : "サブスクリプション申込リンクの作成に失敗しました。"));
            }
            await navigator.clipboard.writeText(result.public_url);
            alert(`${isEnglishBeta ? 'Created and copied the signup link.' : '申込リンクを作成してコピーしました。'}\n${result.public_url}`);
            setSubscriptionDraft(null);
            fetchData();
        } catch (err) {
            alert(err.message);
        } finally {
            setIsCreatingSubscription(false);
        }
    };

    const handleCopySubscriptionUrl = async (token) => {
        const url = `${window.location.origin}/app/subscription/${token}`;
        await navigator.clipboard.writeText(url);
        alert((isEnglishBeta ? "URL copied: " : "URLをコピーしました: ") + url);
    };

    const handleToggleSubscriptionCancel = async (subscription, shouldCancel) => {
        const message = shouldCancel
            ? (isEnglishBeta ? "Schedule this subscription to cancel at the end of the period?" : "このサブスクリプションを期間終了時に解約予約しますか？")
            : (isEnglishBeta ? "Remove the scheduled cancellation for this subscription?" : "このサブスクリプションの解約予約を解除しますか？");
        if (!window.confirm(message)) return;
        try {
            const res = await fetch(`${API_URL}/api/crm/customer-subscriptions/${subscription.id}/cancel`, {
                method: "POST",
                headers: getAuthHeader(true),
                body: JSON.stringify({ client_id: clientId, cancel_at_period_end: shouldCancel })
            });
            const result = await res.json();
            if (!res.ok || !result.success) {
                throw new Error(result.error || (isEnglishBeta ? "Failed to update the subscription." : "サブスクリプションの更新に失敗しました。"));
            }
            alert(result.message || (isEnglishBeta ? "Updated." : "更新しました。"));
            fetchData();
        } catch (err) {
            alert(err.message);
        }
    };

    // 請求書ステータス更新
    const handleInvoiceStatus = async (id, status) => { 
        if (!window.confirm(isEnglishBeta ? `Change status to "${status}"?` : `ステータスを「${status}」に変更しますか？`)) return; 
        try { 
            await fetch(`${API_URL}/api/crm/invoices/${id}/status`, { 
                method: "PUT", 
                headers: getAuthHeader(true), 
                body: JSON.stringify({ client_id: clientId, status }) 
            }); 
            fetchData(); 
        } catch (err) { alert(err.message); } 
    };

    const handleCreateReceipt = (invoice) => {
        setSourceInvoice(invoice);
        setInvoiceType('receipt');
        setShowInvoiceModal(true);
    };

    const handleCopyUrl = (token) => { const url = `${window.location.origin}/app/quotation/${token}`; navigator.clipboard.writeText(url); alert((isEnglishBeta ? "URL copied: " : "URLをコピーしました: ") + url); };

    const filteredInvoices = useMemo(() => {
        if (!data?.invoices) return [];
        if (invoiceFilter === 'all') return data.invoices;
        return data.invoices.filter(inv => inv.type === invoiceFilter);
    }, [data, invoiceFilter]);

    const subscriptionByContractId = useMemo(() => {
        const map = new Map();
        billingSubscriptions.forEach((subscription) => {
            if (!subscription.contract_id) return;
            if (!map.has(subscription.contract_id)) {
                map.set(subscription.contract_id, subscription);
            }
        });
        return map;
    }, [billingSubscriptions]);

    const getBillingIntervalLabel = (subscription) => getBillingIntervalLabelText(subscription, isEnglishBeta);

    if (isLoading) return <div className="fixed inset-0 flex items-center justify-center bg-white z-50"><LoadingSpinner /></div>;
    if (!data) return null;

    // 雛形選択時の処理
    const handleSelectTemplate = (id) => {
        setSelectedTplId(id);
    };

    // 商品選択時の処理（金額を自動セット）
    const handleProductForContract = (productId) => {
        setSelectedProdIds([]);
        const product = billingProducts.find(p => p.id === parseInt(productId));
        if (product) {
            setContractVariables(prev => ({
                ...prev,
                amount: `¥${Number(product.unit_price).toLocaleString()}(税込)`
            }));
        }
    };

    // 契約書発行の実行
    const handleGenerateDoc = async () => {
        // 雛形と商品が選ばれているかチェック
        if (!selectedTplId || selectedProdIds.length === 0) {
            return alert(isEnglishBeta ? "Select one template and at least one product." : "雛形と商品を1つ以上選択してください。");
        }
        
        setIsGenerating(true);
        try {
            const payload = {
                client_id: clientId,
                customer_id: customerId,
                template_id: selectedTplId,
                // ★ 個別ID（selectedProdId）ではなく、複数IDの配列を送る
                product_ids: selectedProdIds, 
                manual_item_name: contractVariables.manual_item_name,
                manual_amount: contractVariables.manual_amount,
                variables: {
                    // 社名や金額などはサーバー側で自動生成するので、
                    // ユーザーが入力した「日」と「期間」だけ送ればOK
                    date: contractVariables.date,
                    period: contractVariables.period
                }
            };
            
            const res = await fetch(`${API_URL}/api/crm/contracts/generate`, {
                method: "POST",
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify(payload)
            });
            
            const result = await res.json();
            if (result.success) {
                alert(isEnglishBeta ? "Contract document issued." : "契約書を発行しました。");
                setSelectedProdIds([]); // 選択をリセット
                fetchData(); // 履歴リストを更新
            } else {
                alert((isEnglishBeta ? "Error: " : "エラー: ") + result.error);
            }
        } catch (e) { 
            console.error(e);
            alert(isEnglishBeta ? "Failed to issue." : "発行失敗"); 
        } finally { 
            setIsGenerating(false); 
        }
    };

    // 契約書の削除実行
    const handleDeleteDoc = async (docId) => {
        if (!window.confirm(isEnglishBeta ? "Delete this contract document? The preview URL will also stop working." : "この契約書データを削除しますか？（プレビューURLも無効になります）")) return;
        
        try {
            const res = await fetch(`${API_URL}/api/crm/contracts/documents/${docId}`, {
                method: "DELETE",
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId })
            });
            const result = await res.json();
            if (result.success) {
                fetchData(); // リスト更新
            }
        } catch (e) { alert(isEnglishBeta ? "Failed to delete." : "削除に失敗しました"); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center sm:items-center z-40 p-0 sm:p-4">
            <div className="bg-white w-full max-w-5xl rounded-none sm:rounded-lg shadow-xl h-full max-h-none sm:h-auto sm:max-h-[95vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-start gap-3 p-4 border-b bg-gray-50 sm:rounded-t-lg">
                    <div className="min-w-0 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <div>
                            <h2 className="text-lg sm:text-xl font-bold text-gray-800 break-words">{data.customer.company_name}</h2>
                            <p className="text-xs text-gray-500">{isEnglishBeta ? 'Created:' : '登録日:'} {new Date(data.customer.created_at).toLocaleDateString(isEnglishBeta ? 'en-US' : undefined)}</p>
                        </div>
                        <div className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-bold border border-purple-200">
                            LTV: ¥{data.customer.ltv ? parseInt(data.customer.ltv).toLocaleString() : 0}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full"><CloseIcon /></button>
                </div>

                {/* Tabs */}
                <div className="flex border-b px-3 sm:px-4 overflow-x-auto">
                    {(isEnglishBeta ? ['info', 'contract', 'contacts', 'invoices', 'timeline'] : ['info', 'contract', 'contracts_doc', 'e_signature', 'contacts', 'invoices', 'timeline']).map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            {tab === 'info' && (isEnglishBeta ? 'Profile' : '基本情報')}
                            {tab === 'contract' && `${isEnglishBeta ? 'Contracts' : '契約管理'} (${contracts.length})`}
                            {tab === 'contracts_doc' && `契約書発行`}
                            {tab === 'e_signature' && `電子署名`}
                            {tab === 'contacts' && `${isEnglishBeta ? 'Contacts' : '担当者'} (${data.contacts.length})`}
                            {tab === 'invoices' && (isEnglishBeta ? 'Sales Docs' : '販売管理')}
                            {tab === 'timeline' && (isEnglishBeta ? 'Activity' : '対応履歴')}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-grow bg-gray-50">
                    {/* 基本情報タブ */}
                    {activeTab === 'info' && (
                        <div className="bg-white p-6 rounded shadow space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Company name' : '会社名'}</label><input className="w-full border p-2 rounded" value={editInfo.company_name || ''} onChange={e => setEditInfo({...editInfo, company_name: e.target.value})} /></div>
                                <div><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Representative' : '代表者名'}</label><input className="w-full border p-2 rounded" value={editInfo.representative_name || ''} onChange={e => setEditInfo({...editInfo, representative_name: e.target.value})} /></div>
                                
                                {/* ★ 追加: 担当者編集 */}
                                <div>
                                    <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Internal owner' : '社内担当者'}</label>
                                    <select 
                                        className="w-full border p-2 rounded bg-white" 
                                        value={editInfo.assigned_user_id || ''} 
                                        onChange={e => setEditInfo({...editInfo, assigned_user_id: e.target.value})}
                                    >
                                        <option value="">{isEnglishBeta ? 'Unassigned' : '担当なし'}</option>
                                        {users && users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                </div>

                                <div><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Phone' : '電話番号'}</label><input className="w-full border p-2 rounded" value={editInfo.phone || ''} onChange={e => setEditInfo({...editInfo, phone: e.target.value})} /></div>
                                <div><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Postal code' : '郵便番号'}</label><input className="w-full border p-2 rounded" value={editInfo.postal_code || ''} onChange={e => setEditInfo({...editInfo, postal_code: e.target.value})} /></div>
                                <div className="col-span-2"><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Address' : '住所'}</label><input className="w-full border p-2 rounded" value={editInfo.address || ''} onChange={e => setEditInfo({...editInfo, address: e.target.value})} /></div>
                                <div><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Tax registration number' : 'インボイス登録番号'}</label><input className="w-full border p-2 rounded" placeholder="T1234..." value={editInfo.registration_number || ''} onChange={e => setEditInfo({...editInfo, registration_number: e.target.value})} /></div>
                                <div><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Payment terms' : '支払条件'}</label><input className="w-full border p-2 rounded" placeholder={isEnglishBeta ? 'End-of-month close, due next month' : '月末締め翌月末払い'} value={editInfo.payment_terms || ''} onChange={e => setEditInfo({...editInfo, payment_terms: e.target.value})} /></div>
                                <div><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Acquisition source' : '獲得経路'}</label><select className="w-full border p-2 rounded bg-white" value={editInfo.acquisition_source || ''} onChange={e => setEditInfo({...editInfo, acquisition_source: e.target.value})}><option value="">{isEnglishBeta ? 'Not selected' : '未選択'}</option>{Object.entries(ACQUISITION_SOURCE_MAP).map(([key]) => <option key={key} value={key}>{getAcquisitionSourceLabel(key, isEnglishBeta)}</option>)}</select></div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Tags (comma separated)' : 'タグ (カンマ区切り)'}</label>
                                    <input 
                                        className="w-full border p-2 rounded" 
                                        placeholder={isEnglishBeta ? 'e.g. existing customer, newsletter' : '例: 既存客, メルマガ希望'} 
                                        value={editInfo.tags || ''} 
                                        onChange={e => setEditInfo({...editInfo, tags: e.target.value})} 
                                    />
                                </div>
                                <div><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Next action date' : '次回アクション日'}</label><input type="date" className="w-full border p-2 rounded" value={editInfo.next_action_date ? editInfo.next_action_date.split('T')[0] : ''} onChange={e => setEditInfo({...editInfo, next_action_date: e.target.value})} /></div>
                                <div className="col-span-2"><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Next action memo' : '次回アクションメモ'}</label><input className="w-full border p-2 rounded" value={editInfo.next_action_note || ''} onChange={e => setEditInfo({...editInfo, next_action_note: e.target.value})} /></div>
                                <div className="col-span-2"><label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Notes' : '備考'}</label><textarea rows="3" className="w-full border p-2 rounded" value={editInfo.notes || ''} onChange={e => setEditInfo({...editInfo, notes: e.target.value})} /></div>
                            </div>
                            <div className="text-right"><button onClick={handleSaveInfo} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">{isEnglishBeta ? 'Save' : '保存する'}</button></div>
                        </div>
                    )}

                    {/* 契約管理タブ */}
                    {activeTab === 'contract' && (
                        <div className="space-y-6">
                            {editingContract ? (
                                <div className="bg-white p-6 rounded shadow space-y-6 border border-blue-200">
                                    <h3 className="font-bold text-lg text-gray-800 border-b pb-2">{editingContract.id ? (isEnglishBeta ? 'Edit Contract' : '契約編集') : (isEnglishBeta ? 'Add Contract' : '新規契約追加')}</h3>
                                    <form onSubmit={handleContractSubmit}>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Contract type' : '契約形態'}</label>
                                                <select className="w-full border p-2 rounded bg-white" value={editingContract.contract_type || 'spot'} onChange={e => setEditingContract({...editingContract, contract_type: e.target.value})}>
                                                    <option value="spot">{getContractTypeLabel('spot', isEnglishBeta)}</option>
                                                    <option value="subscription">{getContractTypeLabel('subscription', isEnglishBeta)}</option>
                                                    <option value="consulting">{getContractTypeLabel('consulting', isEnglishBeta)}</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Amount (monthly / unit)' : '契約金額 (月額/単価)'}</label>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-500">¥</span>
                                                    <input type="number" className="w-full border p-2 rounded" value={editingContract.contract_amount || ''} onChange={e => setEditingContract({...editingContract, contract_amount: e.target.value})} />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Contract status' : '契約ステータス'}</label>
                                                <select className="w-full border p-2 rounded bg-white" value={editingContract.contract_status || 'active'} onChange={e => setEditingContract({...editingContract, contract_status: e.target.value})}>
                                                    <option value="active">{getContractStatusLabel('active', isEnglishBeta)}</option>
                                                    <option value="pending">{isEnglishBeta ? 'Negotiating / Reviewing' : '交渉・検討中'}</option>
                                                    <option value="cancelled">{isEnglishBeta ? 'Canceled' : '解約済み'}</option>
                                                    <option value="completed">{getContractStatusLabel('completed', isEnglishBeta)}</option>
                                                </select>
                                            </div>
                                            <div></div> {/* Spacer */}
                                            <div>
                                                <label className="text-xs font-bold text-gray-500">{isEnglishBeta ? 'Start date' : '契約開始日'}</label>
                                                <input type="date" className="w-full border p-2 rounded" value={editingContract.contract_start_date ? editingContract.contract_start_date.split('T')[0] : ''} onChange={e => setEditingContract({...editingContract, contract_start_date: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-500">{editingContract.contract_type === 'subscription' ? (isEnglishBeta ? 'Next renewal date' : '次回更新日') : (isEnglishBeta ? 'End date' : '契約終了日')}</label>
                                                <input type="date" className="w-full border p-2 rounded" value={editingContract.contract_end_date ? editingContract.contract_end_date.split('T')[0] : ''} onChange={e => setEditingContract({...editingContract, contract_end_date: e.target.value})} />
                                            </div>
                                        </div>
                                        <div className="text-right border-t pt-4 mt-4 flex justify-end gap-2">
                                            <button type="button" onClick={() => setEditingContract(null)} className="px-4 py-2 border rounded text-sm">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                                            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">{isEnglishBeta ? 'Save' : '保存する'}</button>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <>
                                    <div className="flex justify-end">
                                        <button onClick={() => setEditingContract(initialContractForm)} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"><PlusIcon /> {isEnglishBeta ? 'Add Contract' : '契約を追加'}</button>
                                    </div>
                                    <div className="bg-white rounded shadow overflow-hidden">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-gray-100 text-gray-700">
                                                <tr>
                                                    <th className="p-3">{isEnglishBeta ? 'Type' : '形態'}</th>
                                                    <th className="p-3 text-right">{isEnglishBeta ? 'Amount' : '金額'}</th>
                                                    <th className="p-3">{isEnglishBeta ? 'Status' : 'ステータス'}</th>
                                                    <th className="p-3">{isEnglishBeta ? 'Period' : '期間'}</th>
                                                    <th className="p-3">{isEnglishBeta ? 'Stripe Payment' : 'Stripe決済'}</th>
                                                    <th className="p-3 w-24 text-center">{isEnglishBeta ? 'Actions' : '操作'}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {contracts.length === 0 ? (
                                                    <tr><td colSpan="6" className="p-4 text-center text-gray-500">{isEnglishBeta ? 'No contract records yet.' : '契約情報がありません。'}</td></tr>
                                                ) : contracts.map(c => {
                                                    const crmSubscription = subscriptionByContractId.get(c.id);
                                                    const subscriptionStatus = String(crmSubscription?.status || '').toLowerCase();
                                                    const isCancelable = crmSubscription?.stripe_subscription_id && ['active', 'trialing', 'past_due'].includes(subscriptionStatus);
                                                    return (
                                                        <tr key={c.id} className="hover:bg-gray-50">
                                                            <td className="p-3">
                                                                {c.contract_type === 'subscription' ? <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">{getContractTypeLabel('subscription', isEnglishBeta, true)}</span> : 
                                                                 c.contract_type === 'consulting' ? <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">{getContractTypeLabel('consulting', isEnglishBeta, true)}</span> :
                                                                 <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">{getContractTypeLabel('spot', isEnglishBeta, true)}</span>}
                                                            </td>
                                                            <td className="p-3 text-right font-bold">¥{parseInt(c.contract_amount).toLocaleString()}</td>
                                                            <td className="p-3">
                                                                <span className={`px-2 py-1 rounded text-xs ${c.contract_status === 'active' ? 'bg-green-100 text-green-800' : c.contract_status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-gray-200'}`}>
                                                                    {getContractStatusLabel(c.contract_status, isEnglishBeta)}
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-gray-600 text-xs">
                                                                {c.contract_start_date ? new Date(c.contract_start_date).toLocaleDateString() : '-'} ～ <br/>
                                                                {c.contract_end_date ? new Date(c.contract_end_date).toLocaleDateString() : '-'}
                                                            </td>
                                                            <td className="p-3 text-xs">
                                                                {c.contract_type !== 'subscription' ? (
                                                                    <span className="text-gray-400">-</span>
                                                                ) : crmSubscription ? (
                                                                    <div className="space-y-1">
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            <span className={`px-2 py-1 rounded font-bold ${
                                                                                ['active', 'trialing'].includes(subscriptionStatus)
                                                                                    ? 'bg-green-100 text-green-700'
                                                                                    : subscriptionStatus === 'past_due'
                                                                                        ? 'bg-amber-100 text-amber-700'
                                                                                        : subscriptionStatus === 'canceled'
                                                                                            ? 'bg-gray-200 text-gray-600'
                                                                                            : 'bg-blue-100 text-blue-700'
                                                                            }`}>
                                                                                {subscriptionStatus === 'active' ? (isEnglishBeta ? 'Paying' : '決済中') : subscriptionStatus === 'trialing' ? (isEnglishBeta ? 'Trialing' : 'トライアル中') : subscriptionStatus === 'past_due' ? (isEnglishBeta ? 'Past Due' : '支払失敗') : subscriptionStatus === 'canceled' ? (isEnglishBeta ? 'Canceled' : '解約済') : (isEnglishBeta ? 'Not Started' : '未申込')}
                                                                            </span>
                                                                            <span className="text-gray-500">¥{Number(crmSubscription.amount).toLocaleString()} / {getBillingIntervalLabel(crmSubscription)}</span>
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            <button onClick={() => handleCopySubscriptionUrl(crmSubscription.view_token)} className="text-blue-600 hover:underline">{isEnglishBeta ? 'Signup URL' : '申込URL'}</button>
                                                                            {isCancelable && (
                                                                                <button
                                                                                    onClick={() => handleToggleSubscriptionCancel(crmSubscription, !crmSubscription.cancel_at_period_end)}
                                                                                    className="text-red-600 hover:underline"
                                                                                >
                                                                                    {crmSubscription.cancel_at_period_end ? (isEnglishBeta ? 'Remove Cancel Schedule' : '解約予約解除') : (isEnglishBeta ? 'Schedule Cancel' : '解約予約')}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <button onClick={() => openSubscriptionDraft(c)} className="bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 font-bold">
                                                                        {isEnglishBeta ? 'Create Signup Link' : '申込リンク作成'}
                                                                    </button>
                                                                )}
                                                            </td>
                                                            <td className="p-3 text-center flex justify-center gap-2">
                                                                <button onClick={() => setEditingContract(c)} className="text-blue-600 p-1 hover:bg-blue-100 rounded"><EditIcon /></button>
                                                                <button onClick={() => handleDeleteContract(c.id)} className="text-red-600 p-1 hover:bg-red-100 rounded"><TrashIcon /></button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'contracts_doc' && (
                        <div className="space-y-6">
                            {/* --- 📝 契約書発行フォーム --- */}
                            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-indigo-100">
                                <h4 className="font-black text-indigo-900 mb-8 flex items-center gap-3 text-lg">
                                    <SparklesIcon className="text-indigo-500" /> 新規契約書の発行
                                </h4>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                                    
                                    {/* --- 左カラム：内容構成 (3/5) --- */}
                                    <div className="lg:col-span-3 space-y-8">
                                        {/* 1. 雛形選択 */}
                                        <div className="space-y-3">
                                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] px-1 flex items-center gap-2">
                                                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-[10px]">1</span>
                                                使用する雛形
                                            </label>
                                            <select 
                                                className="w-full border-2 border-slate-100 p-4 rounded-2xl bg-slate-50 font-black outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-inner" 
                                                value={selectedTplId} 
                                                onChange={e => setSelectedTplId(e.target.value)}
                                            >
                                                <option value="">契約書マスターから選ぶ...</option>
                                                {contractTemplates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                                            </select>
                                        </div>

                                        {/* 2. 商品選択 */}
                                        <div className="space-y-3">
                                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] px-1 flex items-center gap-2">
                                                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-[10px]">2</span>
                                                紐付ける商品（マスタ）
                                            </label>
                                            <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-2xl border-2 border-slate-100 shadow-inner">
                                                {billingProducts.map(p => (
                                                    <label key={p.id} className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${selectedProdIds.includes(p.id) ? 'bg-white border-indigo-500 shadow-sm' : 'border-transparent hover:bg-white/50'}`}>
                                                        <div className="flex items-center gap-3">
                                                            <input 
                                                                type="checkbox" 
                                                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                                                                checked={selectedProdIds.includes(p.id)}
                                                                onChange={(e) => {
                                                                    if(e.target.checked) setSelectedProdIds([...selectedProdIds, p.id]);
                                                                    else setSelectedProdIds(selectedProdIds.filter(id => id !== p.id));
                                                                }}
                                                            />
                                                            <span className="text-xs font-black text-slate-700">{p.name}</span>
                                                        </div>
                                                        <span className="text-[10px] font-mono font-bold text-slate-400">¥{Number(p.unit_price).toLocaleString()}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {/* 2.5 自由入力（商品選択のすぐ下へ） */}
                                        <div className="bg-indigo-50/50 p-6 rounded-3xl border-2 border-indigo-100/50 space-y-4">
                                            <label className="text-[11px] font-black text-indigo-400 uppercase tracking-[0.2em] px-1 flex items-center gap-2">
                                                <PlusIcon size={14}/> その他自由入力（オプション等）
                                            </label>
                                            <div className="grid grid-cols-3 gap-3">
                                                <div className="col-span-2">
                                                    <input 
                                                        type="text" 
                                                        placeholder="品目名（例：初期設定費用、カスタマイズ等）" 
                                                        className="w-full p-4 bg-white border-2 border-indigo-100 rounded-2xl text-xs font-black outline-none focus:border-indigo-500 shadow-sm"
                                                        value={contractVariables.manual_item_name}
                                                        onChange={e => setContractVariables({...contractVariables, manual_item_name: e.target.value})}
                                                    />
                                                </div>
                                                <div>
                                                    <input 
                                                        type="number" 
                                                        placeholder="金額" 
                                                        className="w-full p-4 bg-white border-2 border-indigo-100 rounded-2xl text-xs font-black outline-none focus:border-indigo-500 shadow-sm"
                                                        value={contractVariables.manual_amount}
                                                        onChange={e => setContractVariables({...contractVariables, manual_amount: e.target.value})}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- 右カラム：最終確認・発行 (2/5) --- */}
                                    <div className="lg:col-span-2 flex flex-col justify-between space-y-8 border-l border-slate-100 lg:pl-10">
                                        
                                        <div className="space-y-8">
                                            {/* 3. 特記事項 */}
                                            <div className="space-y-4">
                                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] px-1 flex items-center gap-2">
                                                    <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-[10px]">3</span>
                                                    その他・特記事項
                                                </label>
                                                <div className="bg-slate-50 p-6 rounded-[2rem] border-2 border-slate-100 space-y-5">
                                                    <div className="grid grid-cols-1 gap-4">
                                                        <div>
                                                            <label className="text-[10px] font-black text-slate-400 mb-1 block">契約締結日</label>
                                                            <input type="date" className="w-full p-3 bg-white border-2 border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-indigo-500" value={contractVariables.date} onChange={e => setContractVariables({...contractVariables, date: e.target.value})} />
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-black text-slate-400 mb-1 block">契約期間</label>
                                                            <input type="text" className="w-full p-3 bg-white border-2 border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-indigo-500" placeholder="例：1年間" value={contractVariables.period} onChange={e => setContractVariables({...contractVariables, period: e.target.value})} />
                                                        </div>
                                                    </div>
                                                    <p className="text-[10px] text-slate-400 leading-relaxed italic px-1">
                                                        ※ 宛先社名、住所、代表者名などは顧客情報の最新データが自動的に反映されます。
                                                    </p>
                                                </div>
                                            </div>

                                            {/* 合計金額プレビュー（発行ボタンのすぐ上へ） */}
                                            <div className="bg-slate-900 p-8 rounded-[2rem] shadow-xl shadow-slate-200 text-white relative overflow-hidden group">
                                                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                                                    <CreditCardIcon />
                                                </div>
                                                <span className="text-[10px] font-black opacity-40 uppercase tracking-[0.2em] block mb-2">Total Amount</span>
                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-3xl font-black font-mono">
                                                        ¥{(
                                                            selectedProdIds.reduce((sum, id) => {
                                                                const p = billingProducts.find(prod => prod.id === id);
                                                                return sum + Number(p?.unit_price || 0); // 👈 ここをNumberで囲む
                                                            }, 0) + 
                                                            Number(contractVariables.manual_amount || 0) // 👈 ここもNumberで囲む
                                                        ).toLocaleString()}
                                                    </span>
                                                    <span className="text-xs font-bold opacity-60">（税込）</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 発行ボタン */}
                                        <button 
                                            onClick={handleGenerateDoc}
                                            disabled={isGenerating || !selectedTplId}
                                            className="group relative w-full py-6 bg-indigo-600 text-white rounded-full font-black shadow-2xl shadow-indigo-200 hover:bg-indigo-700 disabled:bg-slate-200 transition-all flex items-center justify-center gap-3 overflow-hidden"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                            {isGenerating ? "発行中..." : (
                                                <>
                                                    <FileTextIcon />
                                                    この内容で契約書を発行する
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            {/* --- 📜 発行済みドキュメント一覧 --- */}
                            <div className="space-y-4">
                                <h4 className="font-black text-slate-700 flex items-center gap-2 px-2"><FileTextIcon size={16}/> 発行済みドキュメント</h4>
                                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-slate-50 text-slate-400 font-black uppercase tracking-widest">
                                            <tr>
                                                <th className="p-4">書類名称</th>
                                                <th className="p-4">発行日</th>
                                                <th className="p-4">ステータス</th>
                                                <th className="p-4 text-right">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 font-bold">
                                            {customerDocs.length === 0 ? (
                                                <tr><td colSpan="4" className="p-10 text-center text-slate-300 italic">発行済みの書類はありません</td></tr>
                                            ) : customerDocs.map(doc => (
                                                <tr key={doc.id} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="p-4 text-slate-700">{doc.title}</td>
                                                    <td className="p-4 text-slate-400">{new Date(doc.created_at).toLocaleDateString()}</td>
                                                    <td className="p-4">
                                                        <span className="px-3 py-1 bg-green-50 text-green-600 rounded-full text-[10px]">発行済</span>
                                                    </td>
                                                    <td className="p-4 text-right">
                                                        <button 
                                                            onClick={() => window.open(`https://sb-trk.site/contract/${doc.view_token}`, '_blank')}
                                                            className="px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-black transition-all"
                                                        >
                                                            プレビュー / PDF
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDeleteDoc(doc.id)}
                                                            className="p-2 text-slate-300 hover:text-red-500 transition-all"
                                                            title="削除"
                                                        >
                                                            <TrashIcon size={16}/>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 電子署名タブ */}
                    {activeTab === 'e_signature' && (
                        <EContractTab 
                            customerId={customerId}
                            clientId={clientId}
                            user={user}
                            contacts={data.contacts}
                        />
                    )}

                    {/* 担当者タブ */}
                    {activeTab === 'contacts' && (
                        <div className="space-y-6">
                            <div className="grid gap-4 md:grid-cols-2">
                                {data.contacts.map(contact => (
                                    <div key={contact.id} className="bg-white p-4 rounded shadow border flex justify-between items-start">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-bold text-lg">{contact.name}</h4>
                                                {contact.is_primary === 1 && <span className="text-xs bg-blue-100 text-blue-800 px-2 rounded-full">{isEnglishBeta ? 'Primary' : 'メイン'}</span>}
                                            </div>
                                            <p className="text-sm text-gray-600">{contact.department} {contact.position}</p>
                                            <div className="mt-2 text-sm">
                                                <p>📧 <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email || '-'}</a></p>
                                                <p>📞 {contact.phone || '-'}</p>
                                            </div>
                                            {contact.role && <p className="mt-2 text-xs bg-gray-100 inline-block px-2 py-1 rounded">{isEnglishBeta ? 'Role:' : '担当:'} {contact.role}</p>}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <button onClick={() => handleEditContactClick(contact)} className="text-blue-600 hover:bg-blue-50 p-1 rounded"><EditIcon /></button>
                                            <button onClick={() => handleDeleteContact(contact.id)} className="text-gray-400 hover:text-red-500 p-1 rounded"><TrashIcon /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className={`bg-white p-4 rounded shadow border-t-4 ${editingContactId ? 'border-orange-500' : 'border-blue-500'}`}>
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="font-bold">{editingContactId ? (isEnglishBeta ? 'Edit Contact' : '担当者情報を編集') : (isEnglishBeta ? 'Add Contact' : '新規担当者追加')}</h4>
                                    {editingContactId && <button onClick={handleCancelEditContact} className="text-xs text-gray-500 hover:underline">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>}
                                </div>
                                <form onSubmit={handleContactSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <input className="border p-2 rounded" placeholder={isEnglishBeta ? 'Name (required)' : '氏名 (必須)'} required value={newContact.name} onChange={e => setNewContact({...newContact, name: e.target.value})} />
                                    <input className="border p-2 rounded" placeholder={isEnglishBeta ? 'Department' : '部署'} value={newContact.department} onChange={e => setNewContact({...newContact, department: e.target.value})} />
                                    <input className="border p-2 rounded" placeholder={isEnglishBeta ? 'Title' : '役職'} value={newContact.position} onChange={e => setNewContact({...newContact, position: e.target.value})} />
                                    <input className="border p-2 rounded" placeholder={isEnglishBeta ? 'Role (e.g. billing)' : '担当領域 (例: 経理)'} value={newContact.role} onChange={e => setNewContact({...newContact, role: e.target.value})} />
                                    <input className="border p-2 rounded" placeholder="Email" type="email" value={newContact.email} onChange={e => setNewContact({...newContact, email: e.target.value})} />
                                    <input className="border p-2 rounded" placeholder={isEnglishBeta ? 'Phone' : '電話番号'} value={newContact.phone} onChange={e => setNewContact({...newContact, phone: e.target.value})} />
                                    <div className="col-span-2 text-right">
                                        <button type="submit" className={`text-white px-4 py-2 rounded text-sm ${editingContactId ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
                                            {editingContactId ? (isEnglishBeta ? 'Update' : '更新する') : (isEnglishBeta ? 'Add' : '追加する')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* 販売管理タブ */}
                    {activeTab === 'invoices' && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                                    {['all', 'quotation', 'invoice', 'receipt'].map((type) => (
                                        <button
                                            key={type}
                                            onClick={() => setInvoiceFilter(type)}
                                            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                                                invoiceFilter === type 
                                                    ? 'bg-white text-gray-800 shadow-sm' 
                                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            {getInvoiceTypeLabel(type, isEnglishBeta)}
                                        </button>
                                    ))}
                                </div>

                                <div className="flex gap-2">
                                    <button onClick={() => { setSourceInvoice(null); setInvoiceType('quotation'); setShowInvoiceModal(true); }} className="bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded hover:bg-gray-50 text-sm">{isEnglishBeta ? 'Create Quote' : '見積書作成'}</button>
                                    <button onClick={() => { setSourceInvoice(null); setInvoiceType('invoice'); setShowInvoiceModal(true); }} className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm">{isEnglishBeta ? 'Create Invoice' : '請求書作成'}</button>
                                </div>
                            </div>

                            <div className="bg-white rounded shadow overflow-hidden">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-100 text-gray-700"><tr><th className="p-3">{isEnglishBeta ? 'Type' : '種類'}</th><th className="p-3">{isEnglishBeta ? 'No.' : '番号'}</th><th className="p-3">{isEnglishBeta ? 'Issue Date' : '発行日'}</th><th className="p-3 text-right">{isEnglishBeta ? 'Amount (tax incl.)' : '金額(税込)'}</th><th className="p-3 text-center">{isEnglishBeta ? 'Status' : 'ステータス'}</th><th className="p-3 text-center">{isEnglishBeta ? 'Actions' : '操作'}</th></tr></thead>
                                    <tbody className="divide-y">
                                        {filteredInvoices.length > 0 ? filteredInvoices.map(inv => (
                                            <tr key={inv.id} className="hover:bg-gray-50">
                                                <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${inv.type === 'invoice' ? 'bg-blue-100 text-blue-800' : (inv.type === 'quotation' ? 'bg-gray-100' : 'bg-green-100 text-green-800')}`}>{getInvoiceTypeLabel(inv.type, isEnglishBeta)}</span></td>
                                                <td className="p-3 font-mono">{inv.invoice_number}</td>
                                                <td className="p-3">{new Date(inv.issue_date).toLocaleDateString()}</td>
                                                <td className="p-3 text-right font-bold">¥{parseInt(inv.total_amount).toLocaleString()}</td>
                                                <td className="p-3 text-center">
                                                    {inv.type === 'invoice' ? (
                                                        inv.status === 'paid' ? <span className="text-green-600 font-bold">{isEnglishBeta ? 'Paid' : '入金済'}</span> : (inv.status === 'void' ? <span className="text-gray-400">{isEnglishBeta ? 'Void' : '無効'}</span> : <span className="text-red-500 font-bold">{isEnglishBeta ? 'Unpaid' : '未入金'}</span>)
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <button onClick={() => handleCopyUrl(inv.view_token)} className="text-blue-600 hover:underline mr-3">URL</button>
                                                    {inv.type === 'invoice' && (
                                                        inv.status === 'paid' ? (
                                                            <div className="inline-flex gap-2">
                                                                <button onClick={() => handleCreateReceipt(inv)} className="text-green-600 hover:underline text-xs flex items-center gap-1"><ReceiptIcon/> {isEnglishBeta ? 'Receipt' : '領収書'}</button>
                                                                <button onClick={() => handleInvoiceStatus(inv.id, 'issued')} className="text-gray-400 hover:text-red-500 hover:underline text-xs flex items-center gap-1"><UndoIcon/> {isEnglishBeta ? 'Undo' : '取消'}</button>
                                                            </div>
                                                        ) : (
                                                            <button onClick={() => handleInvoiceStatus(inv.id, 'paid')} className="text-green-600 hover:underline text-xs font-bold border border-green-600 px-2 py-1 rounded hover:bg-green-50">{isEnglishBeta ? 'Mark Paid' : '消込'}</button>
                                                        )
                                                    )}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="6" className="p-4 text-center text-gray-500">{isEnglishBeta ? 'No matching records.' : '該当するデータはありません'}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* 対応履歴タブ */}
                    {activeTab === 'timeline' && (
                        <div className="space-y-4">
                            <div className="bg-white p-4 rounded shadow border border-blue-100">
                                <div className="flex gap-2 mb-2 items-start">
                                    <div className="flex-shrink-0 pt-2">
                                        <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">{isEnglishBeta ? 'New' : '新規'}</span>
                                    </div>
                                    <div className="flex-grow space-y-2">
                                        <div className="flex gap-2">
                                            <select className="border rounded p-2 text-sm bg-white" value={timelineStaff} onChange={(e) => setTimelineStaff(e.target.value)}>
                                                <option value="">{isEnglishBeta ? 'Owner' : '担当者'}: {user?.name} ({isEnglishBeta ? 'me' : '自分'})</option>
                                                {users && users.filter(u => u.id !== user?.id).map(u => <option key={u.id} value={u.id}>{isEnglishBeta ? 'Owner' : '担当者'}: {u.name}</option>)}
                                            </select>
                                            <span className="text-xs text-gray-400 self-center">{isEnglishBeta ? 'If not selected, you will be recorded as the owner.' : '※選択しない場合は自分が記録者になります'}</span>
                                        </div>
                                        <input 
                                            className="w-full border rounded p-2 text-sm font-bold" 
                                            placeholder={isEnglishBeta ? 'Title (e.g. first call, follow-up)' : '件名 (例: 初回ヒアリング、定期連絡など)'} 
                                            value={timelineSummary} 
                                            onChange={e => setTimelineSummary(e.target.value)}
                                        />
                                        <textarea 
                                            className="w-full border rounded p-2 text-sm" 
                                            rows="3" 
                                            placeholder={isEnglishBeta ? 'Enter activity details or notes...' : '対応内容やメモを入力してください...'} 
                                            value={timelineNote} 
                                            onChange={e => setTimelineNote(e.target.value)}
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <button onClick={handleSaveTimeline} disabled={!timelineNote || !timelineSummary} className="bg-blue-600 text-white px-6 py-2 rounded text-sm hover:bg-blue-700 disabled:bg-gray-400">{isEnglishBeta ? 'Save Activity' : '記録を保存'}</button>
                                </div>
                            </div>

                            <div className="space-y-4 pl-4 border-l-2 border-gray-200 ml-2">
                                {data.timeline.map(item => (
                                    <div key={item.id} className="relative">
                                        <div className="absolute -left-[21px] top-1 w-3 h-3 bg-gray-400 rounded-full border-2 border-white"></div>
                                        <p className="text-xs text-gray-500 mb-1">
                                            {new Date(item.action_date).toLocaleString()} 
                                            {item.activity_type !== 'note' && <span className="ml-2 bg-gray-200 px-1 rounded">{item.activity_type}</span>}
                                        </p>
                                        
                                        {editingTimelineId === item.id ? (
                                            <div className="bg-white p-3 rounded shadow-sm mt-1 border border-orange-300">
                                                <input 
                                                    className="w-full border rounded p-1 mb-2 text-sm font-bold" 
                                                    value={editTimelineData.summary} 
                                                    onChange={e => setEditTimelineData({...editTimelineData, summary: e.target.value})}
                                                    placeholder={isEnglishBeta ? 'Title' : '件名'}
                                                />
                                                <textarea 
                                                    className="w-full border rounded p-1 text-sm" 
                                                    rows="3" 
                                                    value={editTimelineData.details} 
                                                    onChange={e => setEditTimelineData({...editTimelineData, details: e.target.value})}
                                                    placeholder={isEnglishBeta ? 'Details' : '詳細'}
                                                />
                                                <div className="flex justify-end gap-2 mt-2">
                                                    <button onClick={() => setEditingTimelineId(null)} className="text-xs text-gray-500 border px-2 py-1 rounded hover:bg-gray-100">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                                                    <button onClick={handleUpdateTimeline} className="text-xs text-white bg-orange-500 px-3 py-1 rounded hover:bg-orange-600">{isEnglishBeta ? 'Update' : '更新'}</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="bg-white p-3 rounded shadow-sm mt-1 border group relative hover:border-blue-300 transition-colors">
                                                {item.summary && <p className="font-bold text-base text-gray-800 mb-1">{item.summary}</p>}
                                                <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{item.details}</p>
                                                
                                                <div className="flex justify-between items-end mt-2">
                                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button 
                                                            onClick={() => {
                                                                setEditingTimelineId(item.id);
                                                                setEditTimelineData({ summary: item.summary || "", details: item.details || "" });
                                                            }} 
                                                            className="text-blue-600 hover:bg-blue-50 p-1 rounded" title={isEnglishBeta ? 'Edit' : '編集'}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDeleteTimeline(item.id)} 
                                                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1 rounded" title={isEnglishBeta ? 'Delete' : '削除'}
                                                        >
                                                            <TrashIcon />
                                                        </button>
                                                    </div>
                                                    <p className="text-xs text-gray-400">
                                                        {isEnglishBeta ? 'By:' : '記入:'} {users && (users.find(u => u.id === item.performed_by_user_id)?.name || (isEnglishBeta ? 'Unknown' : '不明'))}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {data.timeline.length === 0 && <p className="text-sm text-gray-500 pl-2">{isEnglishBeta ? 'No activity yet.' : '履歴はありません。'}</p>}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {subscriptionDraft && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white w-full max-w-lg rounded-lg shadow-xl">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="font-bold text-lg">{isEnglishBeta ? 'Create Subscription Signup Link' : 'サブスク申込リンク作成'}</h3>
                            <button onClick={() => setSubscriptionDraft(null)}><CloseIcon /></button>
                        </div>
                        <form onSubmit={handleCreateSubscriptionLink} className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-bold mb-1">{isEnglishBeta ? 'Display name' : '表示名'}</label>
                                <input
                                    className="w-full border p-2 rounded"
                                    value={subscriptionDraft.name}
                                    onChange={(e) => setSubscriptionDraft({ ...subscriptionDraft, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold mb-1">{isEnglishBeta ? 'Amount (tax incl.)' : '金額（税込）'}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="w-full border p-2 rounded"
                                        value={subscriptionDraft.amount}
                                        onChange={(e) => setSubscriptionDraft({ ...subscriptionDraft, amount: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-1">{isEnglishBeta ? 'Billing interval' : '請求間隔'}</label>
                                    <select
                                        className="w-full border p-2 rounded bg-white"
                                        value={subscriptionDraft.interval_unit}
                                        onChange={(e) => setSubscriptionDraft({ ...subscriptionDraft, interval_unit: e.target.value })}
                                    >
                                        <option value="month">{isEnglishBeta ? 'Monthly' : '毎月'}</option>
                                        <option value="year">{isEnglishBeta ? 'Yearly' : '毎年'}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold mb-1">{isEnglishBeta ? 'Interval count' : '間隔数'}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="12"
                                        className="w-full border p-2 rounded"
                                        value={subscriptionDraft.interval_count}
                                        onChange={(e) => setSubscriptionDraft({ ...subscriptionDraft, interval_count: e.target.value })}
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1">{isEnglishBeta ? 'Usually leave this as 1.' : '通常は1のままでOKです。'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-1">{isEnglishBeta ? 'Free trial days' : '無料トライアル日数'}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="365"
                                        className="w-full border p-2 rounded"
                                        value={subscriptionDraft.trial_days}
                                        onChange={(e) => setSubscriptionDraft({ ...subscriptionDraft, trial_days: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-800">
                                {isEnglishBeta ? 'After creation, the signup URL is copied. Once the customer registers a card from that URL, Stripe starts recurring billing.' : '作成後、申込URLをコピーします。顧客がURLからカード登録すると、Stripeのサブスクリプションとして継続課金されます。'}
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setSubscriptionDraft(null)} className="px-4 py-2 border rounded text-sm">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                                <button type="submit" disabled={isCreatingSubscription} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm disabled:bg-gray-400">
                                    {isCreatingSubscription ? (isEnglishBeta ? 'Creating...' : '作成中...') : (isEnglishBeta ? 'Create Signup Link' : '申込リンクを作成')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showInvoiceModal && (
                <InvoiceFormModal 
                    customer={data.customer}
                    clientId={clientId}
                    type={invoiceType}
                    onClose={() => setShowInvoiceModal(false)}
                    onSuccess={() => { setShowInvoiceModal(false); fetchData(); }}
                    users={users}
                    sourceInvoice={sourceInvoice}
                    isEnglishBeta={isEnglishBeta}
                />
            )}
        </div>
    );
}

// 5. 一括操作モーダル
function BulkActionModal({ onClose, onExecute, selectedCount, isEnglishBeta = false }) {
    const [actionType, setActionType] = useState("next_action");
    const [updateValue, setUpdateValue] = useState({ date: "", note: "", status: "", staff: "" });

    const handleSubmit = (e) => {
        e.preventDefault();
        let updates = {};
        if (actionType === 'next_action') {
            updates = { next_action_date: updateValue.date, next_action_note: updateValue.note };
        } else if (actionType === 'status') {
            updates = { status: updateValue.status };
        }
        onExecute(updates);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-md rounded-lg shadow-xl p-6">
                <h3 className="text-lg font-bold mb-4">{isEnglishBeta ? `${selectedCount} selected` : `${selectedCount}件を一括操作`}</h3>
                <div className="mb-4">
                    <select className="w-full border p-2 rounded" value={actionType} onChange={e => setActionType(e.target.value)}>
                        <option value="next_action">{isEnglishBeta ? 'Set next action date' : '次回アクション日を一括設定'}</option>
                        <option value="status">{isEnglishBeta ? 'Change status' : 'ステータスを一括変更'}</option>
                    </select>
                </div>
                {actionType === 'next_action' && (
                    <div className="space-y-3">
                        <input type="date" className="w-full border p-2 rounded" value={updateValue.date} onChange={e => setUpdateValue({...updateValue, date: e.target.value})} />
                        <input type="text" className="w-full border p-2 rounded" placeholder={isEnglishBeta ? 'Memo (optional)' : 'メモ (任意)'} value={updateValue.note} onChange={e => setUpdateValue({...updateValue, note: e.target.value})} />
                    </div>
                )}
                {actionType === 'status' && (
                    <select className="w-full border p-2 rounded" value={updateValue.status} onChange={e => setUpdateValue({...updateValue, status: e.target.value})}>
                        <option value="">{isEnglishBeta ? 'Select status' : '選択してください'}</option><option value="active">{getContractStatusLabel('active', isEnglishBeta)}</option><option value="pending">{getContractStatusLabel('pending', isEnglishBeta)}</option><option value="cancelled">{getContractStatusLabel('cancelled', isEnglishBeta)}</option><option value="completed">{getContractStatusLabel('completed', isEnglishBeta)}</option>
                    </select>
                )}
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="px-4 py-2 border rounded">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                    <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded">{isEnglishBeta ? 'Run' : '実行'}</button>
                </div>
            </div>
        </div>
    );
}

// 6. 検索条件保存モーダル
function SaveSearchModal({ onClose, onSave, currentSearch, isEnglishBeta = false }) {
    const [name, setName] = useState("");
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-md rounded-lg shadow-xl p-6">
                <h3 className="text-lg font-bold mb-4">{isEnglishBeta ? 'Save Current Search' : '現在の検索条件を保存'}</h3>
                <input type="text" className="w-full border p-2 rounded mb-4" placeholder={isEnglishBeta ? 'Tab name (e.g. Today follow-up list)' : 'タブ名 (例: 今日の架電リスト)'} value={name} onChange={e => setName(e.target.value)} autoFocus />
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 border rounded">{isEnglishBeta ? 'Cancel' : 'キャンセル'}</button>
                    <button onClick={() => onSave(name)} disabled={!name} className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-300">{isEnglishBeta ? 'Save' : '保存'}</button>
                </div>
            </div>
        </div>
    );
}

function CustomerImportModal({ clientId, onClose, onCommitted, isEnglishBeta = false }) {
    const [file, setFile] = useState(null);
    const [job, setJob] = useState(null);
    const [rows, setRows] = useState([]);
    const [mode, setMode] = useState('skip');
    const [error, setError] = useState('');
    const [resultMessage, setResultMessage] = useState('');
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isCommitting, setIsCommitting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadTemplate = async () => {
        setError('');
        setIsDownloading(true);
        try {
            const params = new URLSearchParams({ client_id: clientId });
            await downloadAuthenticatedFile(`${API_URL}/api/crm/customers/import/template.csv?${params}`, 'crm_customer_import_template.csv');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsDownloading(false);
        }
    };

    const handlePreview = async (e) => {
        e.preventDefault();
        if (!file) {
            setError(isEnglishBeta ? 'Select a CSV file.' : 'CSVファイルを選択してください。');
            return;
        }

        setIsPreviewing(true);
        setError('');
        setResultMessage('');
        setJob(null);
        setRows([]);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('client_id', clientId);
            const res = await fetch(`${API_URL}/api/crm/customers/import/preview`, {
                method: 'POST',
                headers: getAuthHeader(),
                body: formData
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || (isEnglishBeta ? 'Preview failed.' : 'プレビューに失敗しました。'));
            setJob(data.job);
            setRows(data.rows || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsPreviewing(false);
        }
    };

    const handleCommit = async () => {
        if (!job?.id) return;
        if (!window.confirm(isEnglishBeta ? 'Import this preview?' : 'このプレビュー内容でインポートを実行しますか？')) return;

        setIsCommitting(true);
        setError('');
        setResultMessage('');

        try {
            const res = await fetch(`${API_URL}/api/crm/customers/import/jobs/${job.id}/commit`, {
                method: 'POST',
                headers: getAuthHeader(true),
                body: JSON.stringify({ client_id: clientId, mode })
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || (isEnglishBeta ? 'Import failed.' : 'インポートに失敗しました。'));
            setJob(prev => prev ? {
                ...prev,
                status: 'committed',
                import_mode: mode,
                insert_count: data.result?.inserted ?? prev.insert_count,
                update_count: data.result?.updated ?? prev.update_count,
                skip_count: data.result?.skipped ?? prev.skip_count,
                error_count: data.result?.errors ?? prev.error_count
            } : prev);
            setResultMessage(data.message || (isEnglishBeta ? 'Import completed.' : 'インポートが完了しました。'));
            onCommitted?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsCommitting(false);
        }
    };

    const handleDownloadErrors = async () => {
        if (!job?.id) return;
        setError('');
        setIsDownloading(true);
        try {
            const params = new URLSearchParams({ client_id: clientId });
            await downloadAuthenticatedFile(`${API_URL}/api/crm/customers/import/jobs/${job.id}/errors.csv?${params}`, `crm_customer_import_errors_${job.id}.csv`);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsDownloading(false);
        }
    };

    const isCommitted = job?.status === 'committed';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-6xl rounded-lg shadow-xl max-h-[92vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">{isEnglishBeta ? 'Customer CSV Import' : '顧客CSVインポート'}</h3>
                        <p className="text-xs text-gray-500 mt-1">{isEnglishBeta ? 'Review the preview before importing.' : 'プレビュー確認後に登録します。'}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><CloseIcon /></button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <form onSubmit={handlePreview} className="flex flex-col lg:flex-row gap-3 lg:items-end mb-5">
                        <div className="flex-1">
                            <label className="text-xs font-bold text-gray-500 block mb-1">{isEnglishBeta ? 'CSV file' : 'CSVファイル'}</label>
                            <input
                                type="file"
                                accept=".csv,text/csv"
                                onChange={e => setFile(e.target.files?.[0] || null)}
                                className="w-full border rounded px-3 py-2 text-sm"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleDownloadTemplate}
                            disabled={isDownloading}
                            className="inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            <Download size={16} /> {isEnglishBeta ? 'Template CSV' : '雛形CSV'}
                        </button>
                        <button
                            type="submit"
                            disabled={isPreviewing || !file}
                            className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold px-5 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            {isPreviewing ? <RefreshCw size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                            {isEnglishBeta ? 'Preview' : 'プレビュー'}
                        </button>
                    </form>

                    {error && (
                        <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm flex items-start gap-2">
                            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
                        </div>
                    )}
                    {resultMessage && (
                        <div className="mb-4 p-3 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm flex items-start gap-2">
                            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> {resultMessage}
                        </div>
                    )}

                    {job && (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                                <div className="border rounded p-3"><p className="text-xs text-gray-500">{isEnglishBeta ? 'Rows' : '総行数'}</p><p className="text-xl font-bold">{job.total_rows || 0}</p></div>
                                <div className="border rounded p-3"><p className="text-xs text-gray-500">{isEnglishBeta ? 'New' : '新規'}</p><p className="text-xl font-bold text-emerald-700">{job.insert_count || 0}</p></div>
                                <div className="border rounded p-3"><p className="text-xs text-gray-500">{isCommitted ? (isEnglishBeta ? 'Updated' : '更新') : (isEnglishBeta ? 'Duplicate Candidates' : '重複候補')}</p><p className="text-xl font-bold text-amber-700">{isCommitted ? (job.update_count || 0) : (job.update_candidate_count || 0)}</p></div>
                                <div className="border rounded p-3"><p className="text-xs text-gray-500">{isEnglishBeta ? 'Skipped' : 'スキップ'}</p><p className="text-xl font-bold text-gray-600">{job.skip_count || 0}</p></div>
                                <div className="border rounded p-3"><p className="text-xs text-gray-500">{isEnglishBeta ? 'Errors' : 'エラー'}</p><p className="text-xl font-bold text-red-700">{job.error_count || 0}</p></div>
                            </div>

                            <div className="border rounded p-4 mb-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                                    <label className="text-sm font-bold text-gray-700">{isEnglishBeta ? 'Duplicates' : '重複時'}</label>
                                    <select
                                        value={mode}
                                        onChange={e => setMode(e.target.value)}
                                        disabled={isCommitted}
                                        className="border rounded px-3 py-2 text-sm"
                                    >
                                        {getCustomerImportModeOptions(isEnglishBeta).map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {(job.error_count || 0) > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleDownloadErrors}
                                            disabled={isDownloading}
                                            className="inline-flex items-center gap-2 border border-red-200 text-red-700 font-semibold px-4 py-2 rounded hover:bg-red-50 disabled:opacity-50"
                                        >
                                            <Download size={16} /> {isEnglishBeta ? 'Error CSV' : 'エラーCSV'}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleCommit}
                                        disabled={isCommitting || isCommitted}
                                        className="inline-flex items-center gap-2 bg-emerald-600 text-white font-semibold px-5 py-2 rounded hover:bg-emerald-700 disabled:bg-gray-400"
                                    >
                                        {isCommitting ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                                        {isCommitted ? (isEnglishBeta ? 'Imported' : '実行済み') : (isEnglishBeta ? 'Run Import' : 'インポート実行')}
                                    </button>
                                </div>
                            </div>

                            <div className="border rounded overflow-auto">
                                <table className="w-full text-sm text-left text-gray-600 min-w-[900px]">
                                    <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                                        <tr>
                                            <th className="p-3">{isEnglishBeta ? 'Row' : '行'}</th>
                                            <th className="p-3">{isEnglishBeta ? 'Action' : '判定'}</th>
                                            <th className="p-3">{isEnglishBeta ? 'Company' : '会社名'}</th>
                                            <th className="p-3">{isEnglishBeta ? 'Contact' : '担当者'}</th>
                                            <th className="p-3">{isEnglishBeta ? 'Email' : 'メール'}</th>
                                            <th className="p-3">{isEnglishBeta ? 'Phone' : '電話'}</th>
                                            <th className="p-3">{isEnglishBeta ? 'Tags' : 'タグ'}</th>
                                            <th className="p-3">{isEnglishBeta ? 'Message' : 'メッセージ'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {rows.map(row => {
                                            const normalized = row.normalized || {};
                                            const actionLabel = getCustomerImportActionLabel(row.action, isEnglishBeta);
                                            const message = row.error_message || row.warning_message || '';
                                            return (
                                                <tr key={row.row_number} className="hover:bg-gray-50">
                                                    <td className="p-3 font-mono text-xs">{row.row_number}</td>
                                                    <td className="p-3">
                                                        <span className={`inline-flex px-2 py-1 rounded-full border text-xs font-bold ${getCustomerImportActionClass(row.action)}`}>
                                                            {actionLabel}
                                                        </span>
                                                        {row.duplicate_customer_id && <div className="text-[11px] text-gray-400 mt-1">ID: {row.duplicate_customer_id}</div>}
                                                    </td>
                                                    <td className="p-3 font-semibold text-gray-800">{normalized.company_name || '-'}</td>
                                                    <td className="p-3">{normalized.contact_name || '-'}</td>
                                                    <td className="p-3">{normalized.email || '-'}</td>
                                                    <td className="p-3">{normalized.phone || '-'}</td>
                                                    <td className="p-3">{normalized.tags || '-'}</td>
                                                    <td className={`p-3 ${row.error_message ? 'text-red-700' : 'text-amber-700'}`}>{message || '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {(job.total_rows || 0) > rows.length && (
                                <p className="text-xs text-gray-500 mt-2">{isEnglishBeta ? `Preview shows the first ${rows.length} rows.` : `プレビュー表示は先頭${rows.length}件です。`}</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function CustomerImportHistoryModal({ clientId, onClose, isEnglishBeta = false }) {
    const [jobs, setJobs] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isDownloadingId, setIsDownloadingId] = useState(null);

    const fetchJobs = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ client_id: clientId, limit: 30 });
            const res = await fetch(`${API_URL}/api/crm/customers/import/jobs?${params}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || (isEnglishBeta ? 'Failed to load import history.' : '履歴の取得に失敗しました。'));
            setJobs(data.jobs || []);
            setPagination(data.pagination || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [clientId, isEnglishBeta]);

    useEffect(() => {
        if (clientId) fetchJobs();
    }, [clientId, fetchJobs]);

    const handleDownloadErrors = async (jobId) => {
        setError('');
        setIsDownloadingId(jobId);
        try {
            const params = new URLSearchParams({ client_id: clientId });
            await downloadAuthenticatedFile(`${API_URL}/api/crm/customers/import/jobs/${jobId}/errors.csv?${params}`, `crm_customer_import_errors_${jobId}.csv`);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsDownloadingId(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-5xl rounded-lg shadow-xl max-h-[88vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">{isEnglishBeta ? 'Customer CSV Import History' : '顧客CSVインポート履歴'}</h3>
                        {pagination && <p className="text-xs text-gray-500 mt-1">{isEnglishBeta ? `Total ${pagination.totalItems} records` : `全 ${pagination.totalItems} 件`}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={fetchJobs} className="p-2 text-gray-500 hover:bg-gray-100 rounded" disabled={isLoading}>
                            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                        </button>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><CloseIcon /></button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto">
                    {error && (
                        <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm flex items-start gap-2">
                            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
                        </div>
                    )}

                    {isLoading ? (
                        <LoadingSpinner />
                    ) : jobs.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 border rounded">{isEnglishBeta ? 'No import history yet.' : '履歴がありません。'}</div>
                    ) : (
                        <div className="border rounded overflow-auto">
                            <table className="w-full text-sm text-left text-gray-600 min-w-[860px]">
                                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                                    <tr>
                                        <th className="p-3">{isEnglishBeta ? 'Date' : '日時'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'File' : 'ファイル'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'Status' : '状態'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'Mode' : 'モード'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'Rows' : '総行数'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'New' : '新規'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'Updated / Candidates' : '更新/候補'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'Skipped' : 'スキップ'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'Errors' : 'エラー'}</th>
                                        <th className="p-3">{isEnglishBeta ? 'Output' : '出力'}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {jobs.map(job => (
                                        <tr key={job.id} className="hover:bg-gray-50">
                                            <td className="p-3 whitespace-nowrap">{formatCustomerImportDateTime(job.created_at, isEnglishBeta)}</td>
                                            <td className="p-3 font-semibold text-gray-800">{job.original_filename || '-'}</td>
                                            <td className="p-3">
                                                <span className={`inline-flex px-2 py-1 rounded-full border text-xs font-bold ${job.status === 'committed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                                    {job.status === 'committed' ? (isEnglishBeta ? 'Imported' : '実行済み') : (isEnglishBeta ? 'Preview' : 'プレビュー')}
                                                </span>
                                            </td>
                                            <td className="p-3">{getCustomerImportModeOptions(isEnglishBeta).find(option => option.value === job.import_mode)?.label || '-'}</td>
                                            <td className="p-3">{job.total_rows || 0}</td>
                                            <td className="p-3 text-emerald-700 font-bold">{job.insert_count || 0}</td>
                                            <td className="p-3 text-amber-700 font-bold">{job.status === 'committed' ? (job.update_count || 0) : (job.update_candidate_count || 0)}</td>
                                            <td className="p-3 text-gray-600 font-bold">{job.skip_count || 0}</td>
                                            <td className="p-3 text-red-700 font-bold">{job.error_count || 0}</td>
                                            <td className="p-3">
                                                {(job.error_count || 0) > 0 ? (
                                                    <button
                                                        onClick={() => handleDownloadErrors(job.id)}
                                                        disabled={isDownloadingId === job.id}
                                                        className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 border border-red-200 rounded px-2 py-1 text-xs font-semibold disabled:opacity-50"
                                                    >
                                                        <Download size={14} /> {isEnglishBeta ? 'Error CSV' : 'エラーCSV'}
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- Main Page Component ---
function CustomerLedgerPage({ accountPath = '/account' }) {
    const { user, clientId } = useOutletContext();
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = getLedgerCopy(isEnglishBeta);
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [nextActionDate, setNextActionDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [users, setUsers] = useState([]); 
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [showIssuerSettings, setShowIssuerSettings] = useState(false);
    const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
    const [showContractTemplateModal, setShowContractTemplateModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showImportHistoryModal, setShowImportHistoryModal] = useState(false);
    
    // 新機能用State
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [savedSearches, setSavedSearches] = useState([]);
    const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
    
    // ★ 追加: 商品マスタモーダルと無料プラン判定
    const [showProductModal, setShowProductModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [isFreePlan, setIsFreePlan] = useState(false);

    // Load saved searches
    useEffect(() => {
        const saved = localStorage.getItem(`savedSearches_${clientId}`);
        if (saved) setSavedSearches(JSON.parse(saved));
    }, [clientId]);

    const fetchCustomers = useCallback(async (page = 1, filters = {}) => {
        setIsLoading(true);
        // パラメータ構築
        const params = new URLSearchParams({ client_id: clientId, page, limit: 20 });
        if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
        if (filters.nextActionDate) params.append("next_action_date_from", filters.nextActionDate); 
        
        try {
            const res = await fetch(`${API_URL}/api/crm/customers?${params}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) { 
                setCustomers(data.customers || []); 
                setPagination(data.pagination);
                setIsFreePlan(!!data.isFreePlan); // プラン情報をセット

                // ★ 追加: 制限にかかった場合
                if (data.isLimited) {
                    setShowUpgradeModal(true);
                }
            }
        } catch (err) { console.error(err); }
        finally { setIsLoading(false); }
    }, [clientId]);

    useEffect(() => {
        if (clientId) {
            fetch(`${API_URL}/api/users?client_id=${clientId}`, { headers: getAuthHeader() }).then(res => res.json()).then(data => { if (data.success) setUsers(data.users); });
            fetchCustomers(currentPage, { searchTerm, nextActionDate });
        }
    }, [clientId, currentPage, fetchCustomers]);

    // ★ 修正: 検索ハンドラ (無料プラン制限)
    const handleSearch = (e) => { 
        e.preventDefault(); 
        if (isFreePlan && searchTerm) {
            setShowUpgradeModal(true);
            return;
        }
        setCurrentPage(1); 
        fetchCustomers(1, { searchTerm, nextActionDate }); 
    };

    // Checkbox logic
    const handleCheckAll = (e) => {
        if (e.target.checked) setSelectedIds(new Set(customers.map(c => c.id)));
        else setSelectedIds(new Set());
    };
    const handleCheck = (id) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
        setSelectedIds(newSet);
    };

    // Bulk Update
    const handleBulkUpdate = async (updates) => {
        try {
            const res = await fetch(`${API_URL}/api/crm/customers/bulk-update`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId, ids: Array.from(selectedIds), updates })
            });
            const data = await res.json();
            if (data.success) {
                alert(data.message);
                setShowBulkModal(false);
                setSelectedIds(new Set());
                fetchCustomers(currentPage, { searchTerm, nextActionDate });
            } else { alert(copy.errorPrefix + data.error); }
        } catch (err) { alert(err.message); }
    };

    // Saved Searches
    const handleSaveSearch = (name) => {
        const newSearch = { name, criteria: { searchTerm, nextActionDate } };
        const newSaved = [...savedSearches, newSearch];
        setSavedSearches(newSaved);
        localStorage.setItem(`savedSearches_${clientId}`, JSON.stringify(newSaved));
        setShowSaveSearchModal(false);
    };
    const applySavedSearch = (search) => {
        if (isFreePlan && search.criteria.searchTerm) {
            setShowUpgradeModal(true);
            return;
        }
        setSearchTerm(search.criteria.searchTerm);
        setNextActionDate(search.criteria.nextActionDate);
        fetchCustomers(1, search.criteria);
    };
    const deleteSavedSearch = (index) => {
        const newSaved = savedSearches.filter((_, i) => i !== index);
        setSavedSearches(newSaved);
        localStorage.setItem(`savedSearches_${clientId}`, JSON.stringify(newSaved));
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center mb-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{copy.pageTitle}</h1>
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3">
                    <button onClick={() => setShowImportHistoryModal(true)} className="flex items-center justify-center gap-2 bg-white text-gray-700 border border-gray-300 font-semibold px-3 sm:px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm"><History size={16} /> {copy.importHistory}</button>
                    <button onClick={() => setShowImportModal(true)} className="flex items-center justify-center gap-2 bg-white text-emerald-700 border border-emerald-200 font-semibold px-3 sm:px-4 py-2 rounded hover:bg-emerald-50 transition-colors text-sm"><FileSpreadsheet size={16} /> {copy.csvImport}</button>
                    {/* ★ 追加: 商品マスタ管理ボタン */}
                    <button onClick={() => setShowProductModal(true)} className="flex items-center justify-center gap-2 bg-white text-gray-700 border border-gray-300 font-semibold px-3 sm:px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm"><PackageIcon /> {copy.productMaster}</button>
                    {!isEnglishBeta && (
                        <button onClick={() => setShowContractTemplateModal(true)} className="flex items-center justify-center gap-2 bg-white text-indigo-700 border border-indigo-200 font-semibold px-3 sm:px-4 py-2 rounded hover:bg-indigo-50 transition-colors text-sm"><FileTextIcon /> {copy.contractTemplate}</button>
                    )}
                    <button onClick={() => setShowIssuerSettings(true)} className="flex items-center justify-center gap-2 bg-gray-200 text-gray-700 px-3 sm:px-4 py-2 rounded hover:bg-gray-300 text-sm"><SettingsIcon /> {copy.billingSettings}</button>
                    <button onClick={() => setShowNewCustomerModal(true)} className="flex items-center justify-center gap-2 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded hover:bg-blue-700 text-sm"><PlusIcon /> {copy.newCustomer}</button>
                </div>
            </div>

            {/* 保存された検索条件タブ */}
            {savedSearches.length > 0 && (
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                    {savedSearches.map((search, idx) => (
                        <div key={idx} className="flex items-center bg-blue-50 text-blue-800 px-3 py-1 rounded-full text-sm border border-blue-200">
                            <button onClick={() => applySavedSearch(search)} className="mr-2 font-bold hover:underline">{search.name}</button>
                            <button onClick={() => deleteSavedSearch(idx)} className="text-blue-400 hover:text-red-500">×</button>
                        </div>
                    ))}
                </div>
            )}

            <form onSubmit={handleSearch} className="bg-white p-4 rounded shadow mb-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4 mb-2">
                    <div className="relative flex-grow">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"><SearchIcon /></div>
                        <input type="text" className="w-full pl-10 p-2 border rounded" placeholder={copy.searchPlaceholder} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        {/* ★ 追加: ロックアイコン */}
                        {isFreePlan && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-amber-500">
                                <LockIcon className="h-4 w-4" />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-1 sm:w-1/3 sm:flex-row sm:items-center sm:gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap">{copy.nextAction}</span>
                        <input type="date" className="w-full border p-2 rounded" value={nextActionDate} onChange={e => setNextActionDate(e.target.value)} />
                    </div>
                    <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 font-bold flex items-center justify-center gap-2">
                        {copy.search} {isFreePlan && <span className="text-xs bg-gray-700 px-1 rounded">PRO</span>}
                    </button>
                </div>
                <div className="flex justify-end">
                    <button type="button" onClick={() => setShowSaveSearchModal(true)} className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1"><BookmarkIcon /> {copy.saveCondition}</button>
                </div>
            </form>

            {/* 一括操作バー */}
            {selectedIds.size > 0 && (
                <div className="bg-indigo-50 p-3 rounded mb-4 flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center border border-indigo-200">
                    <span className="font-bold text-indigo-800">{isEnglishBeta ? `${selectedIds.size} ${copy.selected}` : `${selectedIds.size}${copy.selected}`}</span>
                    <button onClick={() => setShowBulkModal(true)} className="bg-indigo-600 text-white px-4 py-2 sm:py-1 rounded hover:bg-indigo-700 flex items-center justify-center gap-2"><CheckSquareIcon /> {copy.runBulk}</button>
                </div>
            )}

            <div className="bg-white rounded shadow overflow-hidden">
                <div className="divide-y md:hidden">
                    {isLoading ? (
                        <div className="p-6"><LoadingSpinner /></div>
                    ) : customers.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">{copy.noCustomers}</div>
                    ) : customers.map(c => (
                        <div key={`${c.id}-mobile`} className={`p-4 ${selectedIds.has(c.id) ? 'bg-indigo-50' : 'bg-white'}`}>
                            <div className="flex items-start gap-3">
                                <input type="checkbox" className="mt-1" checked={selectedIds.has(c.id)} onChange={() => handleCheck(c.id)} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="font-bold text-gray-900 break-words">{c.company_name}</p>
                                            <p className="mt-1 text-xs text-gray-500">{c.representative_name || copy.representativeMissing}</p>
                                        </div>
                                        <button onClick={() => setSelectedCustomerId(c.id)} className="shrink-0 text-blue-600 hover:bg-blue-50 p-2 rounded-full"><EditIcon /></button>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                        {c.assigned_user_name ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                                                <UserIcon /> {c.assigned_user_name}
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-500">{copy.unassigned}</span>
                                        )}
                                        {c.contract_type === 'subscription' ? <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">{getContractTypeLabel('subscription', isEnglishBeta, true)}</span> : <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full">{getContractTypeLabel('spot', isEnglishBeta, true)}</span>}
                                        <span className={`px-2 py-1 rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-200'}`}>{c.status === 'active' ? copy.active : copy.inactive}</span>
                                    </div>
                                    {(c.next_action_date || c.next_action_note) && (
                                        <div className="mt-3 rounded-lg bg-blue-50 p-3 text-xs text-blue-800">
                                            {c.next_action_date && <p className="font-bold">{isEnglishBeta ? 'Next:' : '次回:'} {new Date(c.next_action_date).toLocaleDateString(isEnglishBeta ? 'en-US' : undefined)}</p>}
                                            {c.next_action_note && <p className="mt-1 text-blue-700">{c.next_action_note}</p>}
                                        </div>
                                    )}
                                    <p className="mt-3 text-xs text-gray-400">{copy.lastUpdated}: {new Date(c.updated_at).toLocaleDateString(isEnglishBeta ? 'en-US' : undefined)}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                    <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                        <tr>
                            <th className="p-4 w-10"><input type="checkbox" onChange={handleCheckAll} checked={customers.length > 0 && selectedIds.size === customers.length} /></th>
                            <th className="p-4">{copy.companyName}</th>
                            <th className="p-4">{copy.internalOwner}</th> {/* ★ 追加 */}
                            <th className="p-4">{copy.contractType}</th>
                            <th className="p-4">{copy.representative}</th>
                            <th className="p-4">{copy.status}</th>
                            <th className="p-4 text-blue-700">{copy.nextActionCol}</th>
                            <th className="p-4">{copy.lastUpdated}</th>
                            <th className="p-4 w-20">{copy.details}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {isLoading ? ( <tr><td colSpan="9"><LoadingSpinner /></td></tr> ) : customers.length === 0 ? ( <tr><td colSpan="9" className="p-8 text-center text-gray-500">{copy.noCustomers}</td></tr> ) : customers.map(c => (
                            <tr key={c.id} className={`hover:bg-gray-50 ${selectedIds.has(c.id) ? 'bg-indigo-50' : ''}`}>
                                <td className="p-4"><input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => handleCheck(c.id)} /></td>
                                <td className="p-4 font-bold text-gray-800">{c.company_name}</td>
                                {/* ★ 追加: 担当者表示 */}
                                <td className="p-4">
                                    {c.assigned_user_name ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">
                                            <UserIcon /> {c.assigned_user_name}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 text-xs">{copy.unassigned}</span>
                                    )}
                                </td>
                                <td className="p-4">
                                    {c.contract_type === 'subscription' ? <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">{getContractTypeLabel('subscription', isEnglishBeta, true)}</span> : <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">{getContractTypeLabel('spot', isEnglishBeta, true)}</span>}
                                </td>
                                <td className="p-4">{c.representative_name || '-'}</td>
                                <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${c.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-200'}`}>{c.status === 'active' ? copy.active : copy.inactive}</span></td>
                                <td className="p-4 text-blue-600 font-medium">
                                    {c.next_action_date ? new Date(c.next_action_date).toLocaleDateString(isEnglishBeta ? 'en-US' : undefined) : ''}
                                    {c.next_action_note && <div className="text-xs text-gray-400 truncate w-32">{c.next_action_note}</div>}
                                </td>
                                <td className="p-4">{new Date(c.updated_at).toLocaleDateString(isEnglishBeta ? 'en-US' : undefined)}</td>
                                <td className="p-4"><button onClick={() => setSelectedCustomerId(c.id)} className="text-blue-600 hover:bg-blue-50 p-2 rounded-full"><EditIcon /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {/* ★ 修正: ページネーション (制限付き) */}
                {pagination && (
                    <div className="p-4 border-t flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                        <span className="text-xs text-gray-500">
                            {copy.allCountPrefix} {pagination.totalItems} {copy.allCountSuffix}
                            {isFreePlan && pagination.totalItems > 50 && copy.freeLimit}
                        </span>
                        <Pagination 
                            currentPage={currentPage} 
                            totalPages={pagination.totalPages} 
                            onPageChange={(p) => { setCurrentPage(p); fetchCustomers(p, { searchTerm, nextActionDate }); }}
                            isFreePlan={isFreePlan}
                            onLimitTrigger={() => setShowUpgradeModal(true)}
                            isEnglishBeta={isEnglishBeta}
                        />
                    </div>
                )}
            </div>

            {selectedCustomerId && <CustomerDetailModal customerId={selectedCustomerId} clientId={clientId} onClose={() => setSelectedCustomerId(null)} users={users} user={user} isEnglishBeta={isEnglishBeta} />}
            {showIssuerSettings && <IssuerSettingsModal clientId={clientId} onClose={() => setShowIssuerSettings(false)} isEnglishBeta={isEnglishBeta} />}
            {showBulkModal && <BulkActionModal selectedCount={selectedIds.size} onClose={() => setShowBulkModal(false)} onExecute={handleBulkUpdate} isEnglishBeta={isEnglishBeta} />}
            {showSaveSearchModal && <SaveSearchModal onClose={() => setShowSaveSearchModal(false)} onSave={handleSaveSearch} currentSearch={{ searchTerm, nextActionDate }} isEnglishBeta={isEnglishBeta} />}
            {showImportModal && (
                <CustomerImportModal
                    clientId={clientId}
                    onClose={() => setShowImportModal(false)}
                    onCommitted={() => fetchCustomers(currentPage, { searchTerm, nextActionDate })}
                    isEnglishBeta={isEnglishBeta}
                />
            )}
            {showImportHistoryModal && (
                <CustomerImportHistoryModal
                    clientId={clientId}
                    onClose={() => setShowImportHistoryModal(false)}
                    isEnglishBeta={isEnglishBeta}
                />
            )}
            
            {showNewCustomerModal && (
                <NewCustomerModal 
                    clientId={clientId} 
                    onClose={() => setShowNewCustomerModal(false)}
                    onSuccess={() => { 
                        setShowNewCustomerModal(false); 
                        fetchCustomers(1); 
                        alert(copy.createdCustomer);
                    }}
                    users={users} // ★ 追加
                    isEnglishBeta={isEnglishBeta}
                />
            )}

            {/* ★ 追加: 商品マスタモーダル */}
            {showProductModal && (
                <ProductManagementModal 
                    client_id={clientId} 
                    onClose={() => setShowProductModal(false)} 
                    isEnglishBeta={isEnglishBeta}
                />
            )}

            {/* ★ 追加: アップグレードモーダル */}
            {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} user={user} isEnglishBeta={isEnglishBeta} accountPath={accountPath} />}
                {showContractTemplateModal && <ContractTemplateModal clientId={clientId} onClose={() => setShowContractTemplateModal(false)} />}
        </div>
    );
}

export default CustomerLedgerPage;
